import { useState, useEffect, useRef } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import * as THREE from "three";

/* ── Google Fonts + CSS global ── */
const fontStyle = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Barlow+Condensed:wght@300;400;700&family=Barlow:wght@300;400;500&display=swap');
  * { margin:0; padding:0; box-sizing:border-box; -webkit-tap-highlight-color:transparent; }
  body { background:#0A0A0C; overflow-x:hidden; }

  @keyframes orbitRun {
    from { transform: rotateY(0deg); }
    to   { transform: rotateY(360deg); }
  }
  @keyframes fadeSlideUp {
    from { opacity:0; transform:translateY(16px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes toastIn {
    from { transform:translateX(-50%) translateY(80px); opacity:0; }
    to   { transform:translateX(-50%) translateY(0); opacity:1; }
  }
  @keyframes toastOut {
    from { transform:translateX(-50%) translateY(0); opacity:1; }
    to   { transform:translateX(-50%) translateY(80px); opacity:0; }
  }
  @keyframes waPulse {
    0%   { box-shadow: 0 4px 20px rgba(37,211,102,.5); }
    50%  { box-shadow: 0 4px 28px rgba(37,211,102,.85), 0 0 0 8px rgba(37,211,102,.12); }
    100% { box-shadow: 0 4px 20px rgba(37,211,102,.5); }
  }

  .orbit-slider {
    transform-style: preserve-3d;
    animation: orbitRun 35s linear infinite;
  }
  .orbit-item {
    position:absolute; inset:0; border-radius:14px;
    background:#111115;
    box-shadow: 0 8px 15px rgba(0,0,0,.5), 0 0 0 1px rgba(200,176,122,.3);
    cursor:pointer; display:flex; flex-direction:column;
    padding:6px; overflow:hidden;
    transition: filter .2s, box-shadow .2s;
  }
  .orbit-item:hover {
    filter:brightness(1.08);
    box-shadow: 0 10px 18px rgba(0,0,0,.6), 0 0 0 2px rgba(200,176,122,.5);
  }
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0px 1000px #111116 inset !important;
    -webkit-text-fill-color: #f0e8dc !important;
    caret-color: #f0e8dc;
  }
  ::-webkit-scrollbar { width:4px; }
  ::-webkit-scrollbar-track { background:transparent; }
  ::-webkit-scrollbar-thumb { background:rgba(200,176,122,.3); border-radius:4px; }
  .navbar-slogan-full { display:inline; }
  .navbar-slogan-short { display:none; }
  @media (max-width: 520px) {
    .navbar-slogan-full { display:none; }
    .navbar-slogan-short { display:inline; }
  }
  .triad-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:12px; max-width:1400px; margin:0 auto; padding:0 16px 20px; }
  @media (max-width:640px) { .triad-grid { grid-template-columns:1fr; gap:8px; } }
  .strip-label { display:flex; align-items:center; gap:6px; flex-shrink:0; }
  .strip-label-text { font-family:'Barlow Condensed',sans-serif; font-size:10px; letter-spacing:0.26em; color:#c8b07a; white-space:nowrap; }
  .strip-divider-left { width:1px; height:14px; background:rgba(200,176,122,.2); flex-shrink:0; }
  .strip-pct { font-family:'Bebas Neue',sans-serif; font-size:20px; color:#c8b07a; line-height:1; }
  .strip-off { font-family:'Barlow Condensed',sans-serif; font-size:12px; font-weight:700; color:#f0e8dc; white-space:nowrap; }
  .strip-confirm { font-family:'Barlow Condensed',sans-serif; font-size:10px; color:rgba(37,211,102,.75); white-space:nowrap; }
  .strip-right { display:flex; align-items:center; gap:10px; flex-shrink:0; }
  @media (max-width: 520px) {
    .strip-label-text { display:none; }
    .strip-right { display:none; }
    .strip-pct { font-size:15px; }
    .strip-off { font-size:10px; }
  }
`;

const GOLD = "#c8b07a";
const WA_NUMBER = "5566992036748";
const waLink = (msg = "") =>
  `https://wa.me/${WA_NUMBER}${msg ? `?text=${encodeURIComponent(msg)}` : ""}`;

const PRODUCTS = [
  // ── Articulados & Sensoriais ─────────────────────────────────────────
  { id:8,  name:"ESQUELETO T-REX FLEXÍVEL",    price:44.90, categoria:"Sensoriais · Neurodivergentes", subcategoria:"Dinossauros",       tipoVenda:"compra-direta", img:"imagens_produtos/EsqueletoFlexíveldeTiranossaurRex.png" },
  { id:33, name:"ANQUILOSSAURO",               price:54.90, categoria:"Sensoriais · Neurodivergentes", subcategoria:"Dinossauros",       tipoVenda:"compra-direta", img:"imagens_produtos/Anquilossauro.png" },
  { id:34, name:"ESPINOSSAURO ESQUELÉTICO",    price:54.90, categoria:"Sensoriais · Neurodivergentes", subcategoria:"Dinossauros",       tipoVenda:"compra-direta", img:"imagens_produtos/EspinossauroEsqueletico.png" },
  { id:35, name:"MOSASSAURO ESQUELÉTICO",      price:59.90, categoria:"Sensoriais · Neurodivergentes", subcategoria:"Dinossauros",       tipoVenda:"compra-direta", img:"imagens_produtos/MosassauroEsquelético.png" },
  { id:36, name:"PIRORAPTOR ARTICULADO",       price:49.90, categoria:"Sensoriais · Neurodivergentes", subcategoria:"Dinossauros",       tipoVenda:"compra-direta", img:"imagens_produtos/PiroraptorArticulado.png" },
  { id:37, name:"PTERANODONTE ARTICULADO",     price:52.90, categoria:"Sensoriais · Neurodivergentes", subcategoria:"Dinossauros",       tipoVenda:"compra-direta", img:"imagens_produtos/PteranodonteEsqueleticoArticulado.png" },
  { id:38, name:"TRICERATOPS FLEXÍVEL",        price:54.90, categoria:"Sensoriais · Neurodivergentes", subcategoria:"Dinossauros",       tipoVenda:"compra-direta", img:"imagens_produtos/TriceratopsEsqueletoFlexível.png" },
  { id:39, name:"VELOCIRAPTOR ESQUELÉTICO",    price:49.90, categoria:"Sensoriais · Neurodivergentes", subcategoria:"Dinossauros",       tipoVenda:"compra-direta", img:"imagens_produtos/VelociraptorArticuladoEsquelético.png" },
  { id:19, name:"CUBO ANTIESTRESSE TEXTURIZADO",           price:24.99, precoOriginal:39.99, categoria:"Pedagógicos · STEM", subcategoria:"Jogos Educativos", tipoVenda:"compra-direta", img:"imagens_produtos/cuboangular.png",
    descLonga:"Cubo com superfície texturizada que estimula os receptores táteis das mãos. Além de aliviar ansiedade e estresse, é um recurso educacional comprovado para o desenvolvimento da coordenação motora fina em crianças e adultos.",
    beneficios:["Auxilia na coordenação motora fina","Reduz ansiedade e estresse com estimulação tátil","Indicado para TEA, TDAH e dificuldades de regulação sensorial","Recurso pedagógico para salas de aula e terapias","Textura ergonômica que ocupa as mãos sem distrair"],
  },
  { id:28, name:"SPINNER DE ENGRENAGENS",     price:59.90, categoria:"Sensoriais · Neurodivergentes", subcategoria:"Fidget & Spinners", tipoVenda:"compra-direta", img:"imagens_produtos/SpinnerdeEngrenagensPlanetariasGeometricas.png" },
  { id:41, name:"ZEUS FIDGET SPINNER",        price:54.90, categoria:"Sensoriais · Neurodivergentes", subcategoria:"Fidget & Spinners", tipoVenda:"compra-direta", img:"imagens_produtos/ZeusFidgetSpinnerPesado.png" },
  { id:42, name:"SPINNER",                    price:34.90, categoria:"Sensoriais · Neurodivergentes", subcategoria:"Fidget & Spinners", tipoVenda:"compra-direta", img:"imagens_produtos/spinner.png" },
  { id:58, name:"SPINNER BLOCO EXPLOSIVO",   price:35.00, categoria:"Sensoriais · Neurodivergentes", subcategoria:"Fidget & Spinners", tipoVenda:"compra-direta", img:"imagens_produtos/SpinnerCreeper.png" },
  { id:61, name:"FIDGET ESTRELA EXPANSÍVEL", price:34.99, categoria:"Sensoriais · Neurodivergentes", subcategoria:"Fidget & Spinners", tipoVenda:"compra-direta", img:"imagens_produtos/fidgetEstrelaSensorial.png" },

  { id:40, name:"BAILARINA SENSORIAL",        price:49.90, categoria:"Sensoriais · Neurodivergentes", subcategoria:"Sensoriais",        tipoVenda:"compra-direta", img:"imagens_produtos/bailarinasensorialgiratoria.png" },
  { id:48, name:"JOGO DE EQUILÍBRIO E ATENÇÃO DINOS",  price:69.99, categoria:"Pedagógicos · STEM", subcategoria:"Jogos Educativos", tipoVenda:"compra-direta", img:"imagens_produtos/JogoEquilibrioComDinossauros.png",
    descLonga:"Jogo de empilhamento e equilíbrio com dinossauros coloridos. Desafie a criança a montar a torre sem deixar cair, desenvolvendo concentração e coordenação motora fina de forma lúdica.",
    beneficios:["Desenvolvimento do equilíbrio e coordenação motora","Raciocínio espacial e noção de peso","Estimula a paciência e concentração","Jogo cooperativo ou competitivo entre crianças","Apresenta dinossauros como elemento motivador"],
  },
  { id:49, name:"FORMAS GIRATÓRIAS: COORDENAÇÃO, FOCO E PRECISÃO",         price:80.00, categoria:"Pedagógicos · STEM", subcategoria:"Jogos Educativos", tipoVenda:"compra-direta", img:"imagens_produtos/FormasGiratóriasMotricidadeFina.png",
    descLonga:"Conjunto de formas geométricas giratórias que estimulam a motricidade fina e o reconhecimento de formas. Cada peça gira sobre seu próprio eixo, proporcionando feedback tátil e visual imediato.",
    beneficios:["Motricidade fina e coordenação olho-mão","Reconhecimento e nomeação de formas geométricas","Estimula a curiosidade e a exploração sensorial","Ideal para educação infantil e anos iniciais","Favorece a concentração e atenção sustentada"],
  },

  // ── Educacionais ─────────────────────────────────────────────────────
  { id:18, name:"SISTEMA SOLAR 3D",          price:74.90, categoria:"Pedagógicos · STEM", subcategoria:"Astronomia",          tipoVenda:"compra-direta", img:"imagens_produtos/sistemasolar.png",
    descLonga:"Maquete tridimensional do Sistema Solar com os oito planetas em escala relativa de tamanho. Perfeito para tornar o ensino de astronomia concreto e visual em sala de aula ou em casa.",
    beneficios:["Identificação e ordenação dos planetas","Noção de escala, órbita e distância no espaço","Estimula a curiosidade científica desde cedo","Suporte para aulas de ciências e geografia","Decorativo e educativo ao mesmo tempo"],
  },
  { id:22, name:"PLANETÁRIO",                price:490.00, categoria:"Pedagógicos · STEM", subcategoria:"Astronomia",          tipoVenda:"compra-direta", img:"imagens_produtos/planetario.png",
    descLonga:"Modelo de planetário que reproduz a abóbada celeste com constelações e posições estelares. Um recurso didático completo para explorar o céu noturno de forma tangível.",
    beneficios:["Reconhecimento de constelações e estrelas","Compreensão dos movimentos celestes","Recurso premium para professores de astronomia","Estimula o interesse por física e cosmologia","Excelente para feiras de ciências e projetos escolares"],
  },
  { id:23, name:"BURACO NEGRO",              price:37.90,  categoria:"Pedagógicos · STEM", subcategoria:"Astronomia",          tipoVenda:"compra-direta", img:"imagens_produtos/buraconegro.png",
    descLonga:"Modelo visual de um buraco negro com disco de acreção, que ilustra de forma física como a gravidade extrema curva o espaço-tempo ao redor de um objeto de massa infinita.",
    beneficios:["Introdução ao conceito de gravidade intensa","Visualização da curvatura do espaço-tempo","Desperta o interesse por astrofísica e relatividade","Ótimo ponto de partida para debates científicos","Peça decorativa para ambientes de estudo"],
  },
  { id:24, name:"HORIZONTE DE EVENTOS",      price:64.90,  categoria:"Pedagógicos · STEM", subcategoria:"Astronomia",          tipoVenda:"compra-direta", img:"imagens_produtos/horizontedeeventos.png",
    descLonga:"Representação artística e educativa do horizonte de eventos de um buraco negro, o ponto de não retorno além do qual nem a luz escapa. Um modelo único para explorar os limites da física conhecida.",
    beneficios:["Exploração de conceitos avançados de astrofísica","Compreensão do limite gravitacional extremo","Estimula o pensamento crítico e científico","Ideal para alunos do ensino médio e universitário","Conversa sobre relatividade geral de forma acessível"],
  },
  { id:30, name:"BASE LANÇAMENTO FOGUETES",  price:245.00, categoria:"Pedagógicos · STEM", subcategoria:"Astronomia",          tipoVenda:"compra-direta", img:"imagens_produtos/baselancamentoFoguetesComLeds.png",
    descLonga:"Base de lançamento de foguetes com iluminação LED integrada, que simula uma plataforma espacial real. Recurso completo para projetos de engenharia, física e programas de ciências aplicadas.",
    beneficios:["Introdução à engenharia aeroespacial","Compreensão de propulsão e física do movimento","Iluminação LED para apresentações e feiras","Estimula o trabalho em equipe em projetos STEM","Recurso premium para laboratórios escolares"],
  },
  { id:32, name:"VISUALIZAÇÃO DA GRAVIDADE", price:64.90,  categoria:"Pedagógicos · STEM", subcategoria:"Astronomia",          tipoVenda:"compra-direta", img:"imagens_produtos/visualizacaogravidade.png",
    descLonga:"Modelo interativo que demonstra visualmente como a massa deforma o espaço-tempo, tornando tangível um dos conceitos mais abstratos da física moderna.",
    beneficios:["Visualização concreta da curvatura gravitacional","Introdução à Teoria da Relatividade Geral","Facilita a compreensão de órbitas e trajetórias","Excelente recurso para aulas de física do ensino médio","Desperta o interesse por cosmologia e astronomia"],
  },
  { id:25, name:"HÉLICE DE EULER",           price:59.90,  categoria:"Pedagógicos · STEM", subcategoria:"Física & Engenharia", tipoVenda:"compra-direta", img:"imagens_produtos/HelicedeEuler.png",
    descLonga:"A Hélice de Euler demonstra o paradoxo do disco giratório: ao ser lançada, prece quase parar antes de cair, revelando princípios de conservação de energia e dinâmica rotacional.",
    beneficios:["Demonstração prática de conservação de energia","Introdução à dinâmica rotacional e momento angular","Fenômeno visualmente impactante e memorável","Ideal para aulas de física mecânica","Desperta curiosidade e questionamentos científicos"],
  },
  { id:29, name:"TRANSMISSÃO CIRCULAR",      price:29.99,  categoria:"Pedagógicos · STEM", subcategoria:"Física & Engenharia", tipoVenda:"compra-direta", img:"imagens_produtos/transmissãoMovimentoCircular.png",
    descLonga:"Modelo de transmissão de movimento circular que demonstra como engrenagens e polias transferem força e velocidade, base de toda a mecânica industrial e dos motores.",
    beneficios:["Compreensão de engrenagens e relação de transmissão","Introdução à mecânica e robótica","Demonstra multiplicação e redução de velocidade","Base para projetos de tecnologia e engenharia","Excelente para cursos técnicos e maker"],
  },
  { id:31, name:"CATAPULTA DA VINCI",        price:74.90,  categoria:"Pedagógicos · STEM", subcategoria:"Física & Engenharia", tipoVenda:"compra-direta", img:"imagens_produtos/catapultaLeonardoDaVinci.png",
    descLonga:"Réplica funcional de uma das máquinas de guerra projetadas por Leonardo da Vinci. Demonstra os princípios de alavanca, energia potencial e lançamento de projéteis de forma prática e histórica.",
    beneficios:["Introdução às máquinas simples e alavancas","Compreensão de energia potencial e cinética","Conexão entre história, arte e ciência","Estimula a montagem, criatividade e engenharia","Perfeito para feiras de ciências e projetos STEM"],
  },
  { id:26, name:"ÂNGULOS",                        price:34.99,  categoria:"Pedagógicos · STEM", subcategoria:"Matemática", tipoVenda:"compra-direta", img:"imagens_produtos/angulos.png",
    descLonga:"Conjunto de discos bicolores para ensino de ângulos, com marcações táteis de graduação. Cada peça representa um ângulo diferente, permitindo comparação e medição de forma concreta e visual.",
    beneficios:["Identificação e medição de ângulos (30°, 45°, 90°, 180°)","Comparação e classificação de ângulos","Apoio visual para geometria e trigonometria","Marcações táteis para aprendizes visuais e cinestésicos","Durável para uso intenso em sala de aula"],
  },
  { id:27, name:"FRAÇÕES MONTESSORI",             price:44.99,  categoria:"Pedagógicos · STEM", subcategoria:"Matemática", tipoVenda:"compra-direta", img:"imagens_produtos/FracoesAprendizagemMontessori.png",
  descLonga:"Manipulativo matemático vibrante e interativo. Cada disco é dividido em partes iguais, de 1 inteiro a 1/6, com números gravados de fácil leitura e codificação de cores distinta para cada conjunto de frações.",
  beneficios:["Introdução e comparação de frações","Exploração da equivalência de frações","Como as frações compõem um todo","Adição e subtração de frações na prática","Apoio a aprendizes visuais em matemática"],
},
  { id:50, name:"SPINNERS KIT BÁSICO",            price:112.00, precoOriginal:140.00, desc:"8 spinners · tabuadas do 2 ao 9",    categoria:"Pedagógicos · STEM", subcategoria:"Matemática", tipoVenda:"compra-direta", img:"imagens_produtos/SpinnersDeMultiplicaçãoeDivisão.png",
    descLonga:"Kit com 8 spinners coloridos, cada um com uma tabuada (do 2 ao 9) gravada nas pás. O movimento de girar associa o lúdico à memorização, tornando o aprendizado das tabuadas muito mais eficaz.",
    beneficios:["Memorização das tabuadas de 2 a 9 de forma cinestésica","Associação entre movimento e conteúdo matemático","Estimula a atenção e o foco em crianças agitadas","Pode ser usado em casa, na escola ou em reforço","Alternativa divertida às fichas e cadernos"],
    opcoes:[{label:"Sem caixa organizadora",extra:0},{label:"Com caixa organizadora",extra:20}],
  },
  { id:51, name:"SPINNERS KIT AVANÇADO",          price:128.00, precoOriginal:160.00, desc:"10 spinners · tabuadas do 11 ao 20", categoria:"Pedagógicos · STEM", subcategoria:"Matemática", tipoVenda:"compra-direta", img:"imagens_produtos/SpinnersDeMultiplicaçãoeDivisão.png",
    descLonga:"Kit com 10 spinners para as tabuadas avançadas (do 11 ao 20), conteúdo exigido no ensino fundamental e raramente trabalhado de forma lúdica. Ideal para alunos que já dominam o básico.",
    beneficios:["Domínio das tabuadas de 11 a 20","Desenvolvimento do cálculo mental avançado","Recurso diferenciado para reforço escolar","Complementa o Kit Básico para cobertura completa","Motiva alunos com dificuldade em conteúdo abstrato"],
    opcoes:[{label:"Sem caixa organizadora",extra:0},{label:"Com caixa organizadora",extra:20}],
  },
  { id:52, name:"SPINNERS COMBO MASTER",          price:208.00, precoOriginal:260.00, desc:"18 spinners · tabuadas do 2 ao 20",  categoria:"Pedagógicos · STEM", subcategoria:"Matemática", tipoVenda:"compra-direta", img:"imagens_produtos/SpinnersDeMultiplicaçãoeDivisão.png",
    descLonga:"Conjunto completo com 18 spinners cobrindo todas as tabuadas do 2 ao 20. O kit definitivo para professores, escolas e famílias que querem trabalhar a matemática de forma completa e envolvente.",
    beneficios:["Cobertura total das tabuadas do 2 ao 20","Kit ideal para salas de aula e laboratórios de matemática","Economia em relação à compra separada dos kits","Ótimo para uso coletivo em grupos de alunos","Presente educativo completo para crianças e jovens"],
    opcoes:[{label:"Sem caixa organizadora",extra:0},{label:"Com caixa organizadora",extra:20}],
  },
  { id:53, name:"QUEBRA-CABEÇA NÚMEROS",          price:45.00, categoria:"Pedagógicos · STEM", subcategoria:"Jogos Educativos", tipoVenda:"compra-direta", img:"imagens_produtos/QuebraCabeçaNúmeros.png",
    descLonga:"Quebra-cabeça com peças numéricas que trabalham o reconhecimento de algarismos, sequência lógica e contagem. Cada peça encaixa de forma única, reforçando a associação entre número e quantidade.",
    beneficios:["Reconhecimento e escrita dos algarismos","Noção de sequência numérica e ordem","Desenvolvimento da coordenação motora fina","Introdução à matemática na educação infantil","Jogo de encaixe que desenvolve raciocínio lógico"],
  },
  { id:54, name:"QUEBRA-CABEÇA BRONTOSSAURO",     price:55.00, categoria:"Pedagógicos · STEM", subcategoria:"Jogos Educativos", tipoVenda:"compra-direta", img:"imagens_produtos/MOntessoriQuebraCabeçaDinossauro.png",
    descLonga:"Quebra-cabeça de encaixe no estilo Montessori com a silhueta de um brontossauro. As peças destacáveis incentivam a criança a montar e desmontar, desenvolvendo a percepção espacial e o vocabulário sobre dinossauros.",
    beneficios:["Desenvolvimento da percepção espacial e visual","Coordenação motora fina com peças de encaixe","Introdução à paleontologia e biodiversidade","Método Montessori de aprendizado autônomo","Estimula a paciência e a resolução de problemas"],
  },
  { id:55, name:"QUEBRA-CABEÇA FORMAS EMPILHÁVEIS", price:85.00, categoria:"Pedagógicos · STEM", subcategoria:"Jogos Educativos", tipoVenda:"compra-direta", img:"imagens_produtos/QuebracabeçadeFormasEmpilháveisInfantil.png",
    descLonga:"Quebra-cabeça de formas geométricas empilháveis que combina o desafio do encaixe com o equilíbrio. A criança monta camadas de formas diferentes, desenvolvendo geometria, raciocínio espacial e motricidade.",
    beneficios:["Reconhecimento de formas geométricas em 3D","Raciocínio espacial e noção de volume","Desafio de equilíbrio que estimula concentração","Ideal para educação infantil e anos iniciais","Combina quebra-cabeça com desafio físico"],
  },
  { id:56, name:"QUEBRA-CABEÇA DESLIZANTE LÓGICO", price:75.99, categoria:"Pedagógicos · STEM", subcategoria:"Jogos Educativos", tipoVenda:"compra-direta", img:"imagens_produtos/QuebraCabeçaDeslizanteLogico.png",
    descLonga:"Quebra-cabeça deslizante clássico reformulado com desafios lógicos progressivos. O jogador desliza as peças para reorganizá-las na ordem correta, desenvolvendo raciocínio lógico e planejamento de movimentos.",
    beneficios:["Desenvolvimento do raciocínio lógico e sequencial","Planejamento de movimentos e pensamento estratégico","Desafios progressivos para diferentes níveis","Estimula a persistência e resolução de problemas","Clássico atemporal com alto valor pedagógico"],
  },
  { id:63, name:"QUEBRA-CABEÇA DESLIZANTE NUMÉRICO LÓGICO", price:24.99, categoria:"Pedagógicos · STEM", subcategoria:"Jogos Educativos", tipoVenda:"compra-direta", img:"imagens_produtos/QuebraCabecaDeslizanteNumerico.png",
    descLonga:"Brinquedo de lógica deslizante portátil com números. Deslize as peças para organizar a sequência correta e desafie seu raciocínio lógico em qualquer lugar.",
    beneficios:["Desenvolve raciocínio lógico e sequencial","Portátil: cabe no bolso ou na mochila","Desafio crescente para crianças e adultos","Estimula concentração e paciência","Sem pilha, sem tela, sem limite de uso"],
    opcoes:["Sem chaveiro","Com chaveiro"],
  },
  { id:57, name:"COBRA ALFABETO",                 price:55.00, categoria:"Pedagógicos · STEM", subcategoria:"Jogos Educativos", tipoVenda:"compra-direta", img:"imagens_produtos/CobraAlfabeto.png",
    descLonga:"Cobra articulada com letras do alfabeto em cada segmento. Além de ser um brinquedo sensorial fascinante, serve como ferramenta para reconhecimento de letras, soletração e primeiras palavras.",
    beneficios:["Reconhecimento das letras do alfabeto","Introdução à soletração e formação de palavras","Estimulação sensorial e motricidade fina","Brinquedo articulado que prende a atenção","Pode ser usado como recurso em alfabetização"],
  },

  // ── Chaveiros antistress ──────────────────────────────────────────────
  { id:60, name:"CHAVEIRO PISTÃO ANTI-ESTRESSE", price:24.99, categoria:"Fidgets · Antistress", subcategoria:"Clickers", tipoVenda:"compra-direta", img:"imagens_produtos/ChaveiroPistaoAntiEstresse.png",
    descLonga:"Chaveiro com mecanismo de pistão que simula o movimento real de um motor. Clique repetitivo e satisfatório para aliviar ansiedade e estresse no dia a dia.",
    beneficios:["Alívio de ansiedade com mecanismo de pistão","Estimulação tátil para TEA e TDAH","Compacto para bolso, mochila ou chaveiro","Mecanismo robusto e durável"],
  },
  { id:59, name:"CHAVEIRO QUEIJO CLICK", price:14.99, promo2x:25.00, categoria:"Fidgets · Antistress", subcategoria:"Clickers", tipoVenda:"compra-direta", img:"imagens_produtos/ChaveiroQueijo.png",
    descLonga:"Chaveiro anti-estresse com mecanismo de clique satisfatório. Pequeno, discreto e silencioso o suficiente para usar em qualquer ambiente. Ideal para quem precisa de algo nas mãos para manter o foco.",
    beneficios:["Alívio imediato de ansiedade e estresse","Estimulação tátil discreta para TEA e TDAH","Cabe no bolso, na mochila ou pendurado na chave","Mecanismo durável de clique repetitivo","Leve 2 por R$ 25,00"],
  },
  { id:62, name:"CHAVEIRO CLICKER EXPLOSIVO", price:19.99, promo2x:30.00, categoria:"Fidgets · Antistress", subcategoria:"Clickers", tipoVenda:"compra-direta", img:"imagens_produtos/chaveiroclickersexplosivo.png",
    descLonga:"Chaveiro clicker com design explosivo e mecanismo de clique preciso. O estalo satisfatório acalma a ansiedade e ocupa as mãos sem incomodar ninguém ao redor. Compacto e resistente para uso diário.",
    beneficios:["Alívio imediato de ansiedade e estresse","Clique satisfatório e silencioso para qualquer ambiente","Ideal para TEA e TDAH: estimulação tátil discreta","Cabe no bolso, na mochila ou pendurado nas chaves","Leve 2 por R$ 30,00"],
  },

  // ── Suportes & Organização ───────────────────────────────────────────
  { id:11, name:"SUPORTE PS5",          price:38.90, categoria:"Suportes & Organização", subcategoria:"Games",                    tipoVenda:"compra-direta", img:"imagens_produtos/SuporteparaControledePS5BlocodeGrama.png" },
  { id:43, name:"SUPORTE MULTÍMETRO",  price:15.00, categoria:"Suportes & Organização", subcategoria:"Ferramentas & Utilidades", tipoVenda:"compra-direta", img:"imagens_produtos/suporteparaponteirademultimetro.png" },
  { id:44, name:"ALÇA PARA SACOLA",    price:13.99, categoria:"Suportes & Organização", subcategoria:"Ferramentas & Utilidades", tipoVenda:"compra-direta", img:"imagens_produtos/utilitarioalcaparasacola.png" },
  { id:47, name:"SUPORTE HASHI",       price:19.99, categoria:"Suportes & Organização", subcategoria:"Ferramentas & Utilidades", tipoVenda:"compra-direta", img:"imagens_produtos/suporteparahashi.png" },

  // ── Casa & Decoração ─────────────────────────────────────────────────
  { id:14, name:"VASO CAPIVARA",         price:25.90, categoria:"Casa & Decoração", subcategoria:"Vasos Animais",   tipoVenda:"compra-direta", img:"imagens_produtos/vasocapivara.png" },
  { id:15, name:"VASO DINO",             price:34.90, categoria:"Casa & Decoração", subcategoria:"Vasos Animais",   tipoVenda:"compra-direta", img:"imagens_produtos/vasodeplantadino.png" },
  { id:16, name:"VASO ELEFANTE",         price:44.90, categoria:"Casa & Decoração", subcategoria:"Vasos Animais",   tipoVenda:"compra-direta", img:"imagens_produtos/vasoelefante.png" },
  { id:45, name:"VASO TRONCO",           price:34.90, categoria:"Casa & Decoração", subcategoria:"Vasos Temáticos", tipoVenda:"compra-direta", img:"imagens_produtos/vasotronco.png" },
  { id:46, name:"CONJUNTO VASOS ALIEN",  price:89.90, categoria:"Casa & Decoração", subcategoria:"Vasos Temáticos", tipoVenda:"compra-direta", img:"imagens_produtos/conjuntovasodeplantasalien.png" },

];

const TRIAD_CATS = new Set(["Pedagógicos · STEM","Sensoriais · Neurodivergentes","Fidgets · Antistress"]);
const PRODUTOS_TRIAD = PRODUCTS.filter(p => TRIAD_CATS.has(p.categoria));

function getInitials(name = "") {
  const p = name.trim().split(" ");
  return p.length >= 2 ? (p[0][0] + p[p.length-1][0]).toUpperCase() : name[0]?.toUpperCase() || "?";
}
function fmtPrice(v) { return `R$ ${v.toFixed(2).replace(".", ",")}`; }

function calcDesconto(totalValor) {
  if (totalValor >= 1200) return { pct: 0.40, label: "40% OFF ATACADO", next: null };
  if (totalValor >= 600)  return { pct: 0.30, label: "30% OFF ATACADO", next: 1200 };
  if (totalValor >= 300)  return { pct: 0.20, label: "20% OFF ATACADO", next: 600  };
  return null;
}
function getStoredUser() { try { const s = localStorage.getItem("3dp_user"); return s ? JSON.parse(s) : null; } catch { return null; } }
function storeUser(u) { try { localStorage.setItem("3dp_user", JSON.stringify(u)); } catch {} }
function removeUser() { try { localStorage.removeItem("3dp_user"); } catch {} }
function catId(cat) {
  return "cat-" + cat.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

/* ══════════════════════════════
   FUNDO THREE.JS
══════════════════════════════ */
function ThreeBackground() {
  const mountRef = useRef(null);

  useEffect(() => {
    const el = mountRef.current;
    if (!el) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0A0A0C);

    const camera = new THREE.PerspectiveCamera(45, el.clientWidth / el.clientHeight, 0.1, 1000);
    camera.position.set(0, 1.2, 10);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(el.clientWidth, el.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    el.appendChild(renderer.domElement);

    // Grid
    const grid = new THREE.GridHelper(24, 32, 0xffffff, 0xaaaaaa);
    grid.position.y = -2.3;
    grid.material.transparent = true;
    grid.material.opacity = 0.4;
    scene.add(grid);

    // Estrelas
    const starsGeo = new THREE.BufferGeometry();
    const count = 800;
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      pos[i*3]   = (Math.random() - 0.5) * 160;
      pos[i*3+1] = (Math.random() - 0.5) * 60;
      pos[i*3+2] = (Math.random() - 0.5) * 70 - 30;
    }
    starsGeo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    const starsMat = new THREE.PointsMaterial({ color: 0xdddddd, size: 0.08, transparent: true, opacity: 0.5 });
    const stars = new THREE.Points(starsGeo, starsMat);
    scene.add(stars);

    let animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      grid.rotation.y  += 0.0008;
      stars.rotation.y += 0.0003;
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      if (!el) return;
      camera.aspect = el.clientWidth / el.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(el.clientWidth, el.clientHeight);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", onResize);
      renderer.dispose();
      if (el.contains(renderer.domElement)) el.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div ref={mountRef} style={{
      position:"fixed", top:0, left:0, width:"100%", height:"100%",
      zIndex:0, pointerEvents:"none",
    }}/>
  );
}

/* ══════════════════════════════
   TOAST
══════════════════════════════ */
function Toast({ msg, icon, visible }) {
  return (
    <div style={{
      position:"fixed", bottom:24, left:"50%",
      transform:"translateX(-50%)",
      background:"rgba(16,16,20,.97)",
      border:"1px solid rgba(200,176,122,.4)",
      borderRadius:40, padding:"10px 22px",
      fontFamily:"'Barlow Condensed',sans-serif",
      fontSize:13, letterSpacing:"0.1em", color:"#f0e8dc",
      zIndex:9999, whiteSpace:"nowrap",
      boxShadow:"0 8px 24px rgba(0,0,0,.5)",
      animation: visible
        ? "toastIn .4s cubic-bezier(.22,1,.36,1) forwards"
        : "toastOut .35s ease forwards",
      pointerEvents:"none",
    }}>
      {icon} {msg}
    </div>
  );
}

/* ══════════════════════════════
   NAVBAR
══════════════════════════════ */
function NavBtn({ onClick, children, title }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} title={title} aria-label={title}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        width:42, height:42, borderRadius:40,
        border: hov ? "1px solid rgba(200,176,122,.5)" : "1px solid rgba(240,235,225,.15)",
        background: hov ? "rgba(200,176,122,.15)" : "rgba(240,235,225,.02)",
        display:"flex", alignItems:"center", justifyContent:"center",
        cursor:"pointer", transition:"all .2s", position:"relative",
      }}
    >{children}</button>
  );
}

function Navbar({ user, onLoginClick, onProfileClick, onCartClick, cartCount }) {
  return (
    <nav style={{
      position:"fixed", top:0, left:0, right:0, height:65, zIndex:150,
      background:"rgba(8,8,10,.87)", backdropFilter:"blur(18px)",
      borderBottom:"1px solid rgba(200,180,140,.25)",
      display:"flex", alignItems:"center", justifyContent:"space-between",
      padding:"0 20px", fontFamily:"'Barlow Condensed',sans-serif",
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        <img src="logo.jpg" alt="Hybridbots logo"
          onError={e => { e.target.onerror=null; e.target.src="https://placehold.co/200x200?text=3DP"; }}
          style={{ height:42, borderRadius:10, border:"1px solid rgba(200,180,140,.5)" }}
        />
        <span style={{ borderLeft:"1px solid rgba(200,176,122,.4)", paddingLeft:12 }}>
          <span className="navbar-slogan-full" style={{ fontSize:10, letterSpacing:"0.3em", color:GOLD }}>
            <strong style={{ color:"#fff" }}>HYBRIDBOTS</strong> • 3D CRIATIVO E EDUCACIONAL
          </span>
          <span className="navbar-slogan-short" style={{ fontSize:13, letterSpacing:"0.25em", color:"#fff", fontFamily:"'Bebas Neue',sans-serif" }}>
            HYBRIDBOTS
          </span>
        </span>
      </div>

      <div style={{ display:"flex", gap:10, alignItems:"center" }}>
        <NavBtn onClick={() => window.open(waLink(), "_blank")} title="WhatsApp">
          <svg viewBox="0 0 24 24" fill="none" stroke="#e0d6ca" strokeWidth="1.4" width={18} height={18}>
            <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/>
          </svg>
        </NavBtn>

        {user ? (
          <button onClick={onProfileClick} title={user.name} style={{
            width:42, height:42, borderRadius:40,
            border:"2px solid "+GOLD,
            background:"linear-gradient(135deg,rgba(200,176,122,.3),rgba(200,176,122,.1))",
            display:"flex", alignItems:"center", justifyContent:"center",
            cursor:"pointer", color:GOLD,
            fontFamily:"'Bebas Neue',sans-serif", fontSize:16, position:"relative",
          }}>
            {getInitials(user.name)}
            <span style={{ position:"absolute", bottom:1, right:1, width:9, height:9, background:"#4CAF50", borderRadius:"50%", border:"1.5px solid #0a0a0c" }}/>
          </button>
        ) : (
          <NavBtn onClick={onLoginClick} title="Entrar">
            <svg viewBox="0 0 24 24" fill="none" stroke="#e0d6ca" strokeWidth="1.4" width={18} height={18}>
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </NavBtn>
        )}

        <NavBtn onClick={onCartClick} title="Carrinho">
          <svg viewBox="0 0 24 24" fill="none" stroke="#e0d6ca" strokeWidth="1.4" width={18} height={18}>
            <circle cx="9" cy="21" r="1.5"/><circle cx="19" cy="21" r="1.5"/>
            <path d="M2 3h2l2.7 12.4a2 2 0 0 0 2 1.6h9.6a2 2 0 0 0 2-1.6L22 7H5"/>
          </svg>
          {cartCount > 0 && (
            <span style={{ position:"absolute", top:-5, right:-5, minWidth:18, height:18, background:GOLD, borderRadius:40, fontSize:9, fontWeight:"bold", color:"#0a0a0c", display:"flex", alignItems:"center", justifyContent:"center", padding:"0 4px" }}>
              {cartCount}
            </span>
          )}
        </NavBtn>
      </div>
    </nav>
  );
}

/* ══════════════════════════════
   CARROSSEL CSS 3D
══════════════════════════════ */
function Carousel({ products, onSelect }) {
  // Produtos em destaque no carrossel (IDs explícitos para controle total)
  const DESTAQUE_IDS = [18, 50, 31, 26, 40, 28, 58, 41, 59];
  const vitrine = DESTAQUE_IDS.map(id => products.find(p => p.id === id)).filter(Boolean);
  const qty = vitrine.length;
  return (
    <div style={{
      position:"relative", zIndex:10, width:"100%", height:240,
      display:"flex", alignItems:"center", justifyContent:"center",
      perspective:700, marginTop:75, marginBottom:5,
    }}>
      <div className="orbit-slider"
        style={{ position:"relative", width:120, height:170, transformStyle:"preserve-3d" }}>
        {vitrine.map((prod, idx) => (
          <div key={prod.id} className="orbit-item"
            onClick={() => onSelect(prod)}
            style={{ transform:`rotateY(${(idx/qty)*360}deg) translateZ(200px)` }}>
            <div style={{ flex:1, borderRadius:10, overflow:"hidden", background:"#1a1a1e" }}>
              <img src={prod.img} alt={prod.name} loading="lazy"
                style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}/>
            </div>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:9, color:"#f0e8dc", textAlign:"center", marginTop:4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{prod.categoria}</div>
            <button onClick={e => { e.stopPropagation(); onSelect(prod); }}
              style={{ background:"rgba(200,176,122,.2)", border:"1px solid rgba(200,176,122,.6)", borderRadius:30, padding:"2px 0", width:"100%", marginTop:3, fontFamily:"'Barlow Condensed',sans-serif", fontSize:7, letterSpacing:"0.1em", color:"#f0e8dc", cursor:"pointer" }}>
              VER CATEGORIA
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════
   FILTRO (BUSCA + CATEGORIA)
══════════════════════════════ */
function TriadSection({ onFiltroCategoria }) {
  const pilares = [
    {
      cat: "Pedagógicos · STEM",
      cor: "#c8b07a",
      tagline: "A criança aprende fazendo. Não apenas vendo.",
      desc: "Pesquisas em neuroeducação comprovam: o aprendizado concreto e tátil é até 3x mais eficaz que o teórico. Nossos kits são a ponte entre o abstrato e o real, usados em escolas, clínicas e programas STEM.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width={26} height={26}>
          <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/>
        </svg>
      ),
    },
    {
      cat: "Sensoriais · Neurodivergentes",
      cor: "#7ab8c8",
      tagline: "Neurocientificamente comprovado.",
      desc: "Para TEA, TDAH, ansiedade e estresse. Equipam salas de acolhimento e inclusão com valor institucional real. Indicado por terapeutas e adquirido por escolas que levam a inclusão a sério.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width={26} height={26}>
          <path d="M12 2a4 4 0 014 4c0 1.5-.8 2.8-2 3.5V12l2 2-2 2v1.5c1.2.7 2 2 2 3.5a4 4 0 01-8 0c0-1.5.8-2.8 2-3.5V16l-2-2 2-2V9.5A4 4 0 018 6a4 4 0 014-4z"/>
        </svg>
      ),
    },
    {
      cat: "Fidgets · Antistress",
      cor: "#b07ac8",
      tagline: "Alívio de estresse mecânico no bolso",
      desc: "Cubos, clickers e pistões com mecanismo real. O movimento repetitivo acalma, regula e ocupa as mãos. Discreto o suficiente para qualquer ambiente.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width={26} height={26}>
          <circle cx="7.5" cy="15.5" r="5.5"/><path d="M21 2l-9.6 9.6"/><path d="M15.5 7.5l3 3"/>
        </svg>
      ),
    },
  ];

  return (
    <div style={{ padding:"28px 0 8px" }}>
      <div style={{ textAlign:"center", marginBottom:16 }}>
        <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:9, letterSpacing:"0.3em", color:"rgba(200,176,122,.5)" }}>NOSSAS ESPECIALIDADES</span>
      </div>
      <div className="triad-grid">
        {pilares.map(p => (
          <button key={p.cat} onClick={() => onFiltroCategoria(p.cat)}
            style={{ background:"rgba(10,10,14,.42)", backdropFilter:"blur(4px)", border:`1px solid ${p.cor}50`, borderRadius:16, padding:"20px 18px", cursor:"pointer", textAlign:"left", transition:"all .2s", display:"flex", flexDirection:"column", gap:10 }}
            onMouseEnter={e => { e.currentTarget.style.background=`rgba(10,10,14,.62)`; e.currentTarget.style.border=`1px solid ${p.cor}99`; }}
            onMouseLeave={e => { e.currentTarget.style.background="rgba(10,10,14,.42)"; e.currentTarget.style.border=`1px solid ${p.cor}50`; }}>
            <div style={{ color:p.cor, display:"flex", alignItems:"center", gap:10 }}>
              {p.icon}
              <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:9, letterSpacing:"0.22em", color:p.cor }}>{p.cat.toUpperCase()}</span>
            </div>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:"#f0e8dc", lineHeight:1.1 }}>{p.tagline}</div>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:13, color:"rgba(240,232,220,.85)", lineHeight:1.5 }}>{p.desc}</div>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:12, color:p.cor, letterSpacing:"0.12em", marginTop:4, fontWeight:700 }}>
              VER PRODUTOS →
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function FiltroBar({ products, filtroTexto, onFiltroTexto, filtroCategoria, onFiltroCategoria }) {
  const categorias = [...new Set(products.map(p => p.categoria))];
  const [inputFocus, setInputFocus] = useState(false);

  return (
    <div style={{
      position:"sticky", top:65, zIndex:100,
      background:"rgba(8,8,10,.94)", backdropFilter:"blur(18px)",
      borderBottom:"1px solid rgba(200,176,122,.2)",
      padding:"12px 16px 14px",
    }}>
      <div style={{ maxWidth:900, margin:"0 auto", display:"flex", flexDirection:"column", gap:10 }}>
        {/* Campo de busca */}
        <div style={{ position:"relative" }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="rgba(200,176,122,.5)" strokeWidth="1.5"
            width={15} height={15}
            style={{ position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}>
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            placeholder="Buscar produto..."
            aria-label="Buscar produto"
            value={filtroTexto}
            onChange={e => onFiltroTexto(e.target.value)}
            onFocus={() => setInputFocus(true)}
            onBlur={() => setInputFocus(false)}
            style={{
              width:"100%", background:"rgba(255,255,255,.04)",
              border: inputFocus
                ? "1px solid rgba(200,176,122,.7)"
                : "1px solid rgba(200,176,122,.25)",
              borderRadius:40,
              padding:"9px 38px",
              fontFamily:"'Barlow Condensed',sans-serif", fontSize:14,
              color:"#f0e8dc", outline:"none", letterSpacing:"0.05em",
              transition:"border .2s",
            }}
          />
          {filtroTexto && (
            <button type="button" onClick={() => onFiltroTexto("")} aria-label="Limpar busca"
              style={{ position:"absolute", right:14, top:"50%", transform:"translateY(-50%)", cursor:"pointer", background:"none", border:"none", padding:0, display:"flex" }}>
              <svg viewBox="0 0 24 24" fill="none"
                stroke="rgba(200,176,122,.5)" strokeWidth="2" width={14} height={14} aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          )}
        </div>

        {/* Pills de categoria */}
        <div style={{ display:"flex", gap:6, flexWrap:"nowrap", overflowX:"auto", paddingBottom:2 }}>
          {["", ...categorias].map(cat => {
            const ativa = filtroCategoria === cat;
            return (
              <button key={cat || "__todas__"} onClick={() => onFiltroCategoria(cat)}
                style={{
                  padding:"6px 16px", borderRadius:40, cursor:"pointer",
                  fontFamily:"'Barlow Condensed',sans-serif", fontSize:12,
                  letterSpacing:"0.1em", transition:"all .2s",
                  background: ativa ? GOLD : "rgba(255,255,255,.04)",
                  border: ativa ? "1px solid "+GOLD : "1px solid rgba(200,176,122,.25)",
                  color: ativa ? "#0a0a0c" : "rgba(240,232,220,.65)",
                  fontWeight: ativa ? "bold" : "normal",
                }}>
                {cat === "" ? "TODOS" : cat.toUpperCase()}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════
   GRID DE PRODUTOS
══════════════════════════════ */
function ProductCard({ prod, onSelect, favoritos = [], onFavorito, onBuy, cart = [], user }) {
  const [hov, setHov] = useState(false);
  const inCart = cart.some(i => i.id === prod.id);
  return (
    <div onClick={() => onSelect(prod)}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? "rgba(0,0,0,.4)" : "rgba(0,0,0,.25)",
        backdropFilter:"blur(4px)", borderRadius:12, padding:7,
        border: hov ? "1px solid rgba(200,176,122,.8)" : "1px solid rgba(200,176,122,.3)",
        transform: hov ? "translateY(-3px)" : "none",
        transition:"all .2s", cursor:"pointer", display:"flex", flexDirection:"column",
      }}>
      <div style={{ width:"100%", aspectRatio:"1/1", borderRadius:12, overflow:"hidden", background:"rgba(0,0,0,.3)" }}>
        <img src={prod.img} alt={prod.name} loading="lazy" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
      </div>
      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:12, color:"#f0e8dc", marginTop:4, textAlign:"center", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{prod.name}</div>
      {prod.desc && <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:10, color:"rgba(240,232,220,.55)", textAlign:"center", margin:"1px 0", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{prod.desc}</div>}
      {prod.precoOriginal && (
        <div style={{ textAlign:"center", margin:"2px 0 0" }}>
          <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:9, color:"rgba(240,232,220,.4)", textDecoration:"line-through" }}>{fmtPrice(prod.precoOriginal)}</span>
        </div>
      )}
      <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:11, fontWeight:"bold", color:GOLD, textAlign:"center", margin:"1px 0" }}>{fmtPrice(prod.price)}</div>
      {prod.precoOriginal && <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:9, fontWeight:700, color:"#0a0a0c", background:"#e05050", borderRadius:20, padding:"2px 7px", textAlign:"center", margin:"1px auto 0", width:"fit-content", letterSpacing:"0.05em" }}>POR TEMPO LIMITADO</div>}
      {prod.promo2x && <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:9, fontWeight:700, color:"#0a0a0c", background:"#25D366", borderRadius:20, padding:"2px 7px", textAlign:"center", margin:"1px auto 0", width:"fit-content", letterSpacing:"0.05em" }}>LEVE 2 POR {fmtPrice(prod.promo2x)}</div>}
      <div style={{ display:"flex", gap:4, marginTop:4 }}>
        <button onClick={e => { e.stopPropagation(); onSelect(prod); }}
          style={{ flex:1, background: prod.tipoVenda === "sob-consulta" ? "rgba(37,211,102,.15)" : "rgba(200,176,122,.2)", border: prod.tipoVenda === "sob-consulta" ? "1px solid rgba(37,211,102,.5)" : "1px solid rgba(200,176,122,.6)", borderRadius:30, padding:"3px 0", fontFamily:"'Barlow Condensed',sans-serif", fontSize:8, letterSpacing:"0.1em", color:"#f0e8dc", cursor:"pointer" }}>
          {prod.tipoVenda === "sob-consulta" ? "CONSULTAR" : "SAIBA MAIS"}
        </button>
        <button onClick={e => { e.stopPropagation(); onFavorito && onFavorito(prod.id); }}
          title={favoritos.includes(prod.id) ? "Remover dos favoritos" : "Adicionar aos favoritos"}
          style={{ width:26, height:26, borderRadius:"50%", border: favoritos.includes(prod.id) ? "1px solid rgba(220,80,80,.6)" : "1px solid rgba(200,176,122,.4)", background: favoritos.includes(prod.id) ? "rgba(220,80,80,.15)" : "rgba(200,176,122,.08)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <svg viewBox="0 0 24 24" fill={favoritos.includes(prod.id) ? "#e05050" : "none"} stroke={favoritos.includes(prod.id) ? "#e05050" : "rgba(200,176,122,.7)"} strokeWidth="1.8" width={13} height={13}>
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
        </button>
        {prod.tipoVenda !== "sob-consulta" && (
          <button onClick={e => { e.stopPropagation(); onBuy && onBuy({ ...prod, quantidade:1, cor:null, textoPersonalizado:null, observacoes:null, opcao: prod.opcoes ? (typeof prod.opcoes[0] === "object" ? prod.opcoes[0].label : prod.opcoes[0]) : null, opcaoExtra: prod.opcoes && typeof prod.opcoes[0] === "object" ? prod.opcoes[0].extra : 0 }); }}
            title="Adicionar ao carrinho"
            style={{ width:26, height:26, borderRadius:"50%", border: inCart ? "1px solid #25D366" : "1px solid rgba(200,176,122,.4)", background: inCart ? "rgba(37,211,102,.18)" : "rgba(200,176,122,.08)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"all .2s" }}>
            <svg viewBox="0 0 24 24" fill="none" stroke={inCart ? "#25D366" : "rgba(200,176,122,.7)"} strokeWidth="1.8" width={13} height={13}>
              <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

function SubcatTabs({ subcats, active, onSelect }) {
  return (
    <div style={{ display:"flex", gap:6, maxWidth:1400, margin:"0 auto 14px", flexWrap:"wrap" }}>
      <button
        onClick={() => onSelect(null)}
        style={{
          fontFamily:"'Barlow Condensed',sans-serif", fontSize:12, letterSpacing:"0.08em",
          padding:"5px 16px", borderRadius:20, cursor:"pointer", transition:"all .2s",
          border: !active ? `1.5px solid ${GOLD}` : "1.5px solid rgba(200,176,122,.25)",
          background: !active ? "rgba(200,176,122,.14)" : "rgba(255,255,255,.04)",
          color: !active ? GOLD : "rgba(240,232,220,.45)",
        }}
      >TODOS</button>
      {subcats.map(sub => (
        <button
          key={sub}
          onClick={() => onSelect(sub)}
          style={{
            fontFamily:"'Barlow Condensed',sans-serif", fontSize:12, letterSpacing:"0.08em",
            padding:"5px 16px", borderRadius:20, cursor:"pointer", transition:"all .2s",
            border: active === sub ? `1.5px solid ${GOLD}` : "1.5px solid rgba(200,176,122,.25)",
            background: active === sub ? "rgba(200,176,122,.14)" : "rgba(255,255,255,.04)",
            color: active === sub ? GOLD : "rgba(240,232,220,.45)",
          }}
        >{sub.toUpperCase()}</button>
      ))}
    </div>
  );
}

function ProductGrid({ products, onSelect, favoritos = [], onFavorito, onBuy, cart = [], user }) {
  const [activeSubcat, setActiveSubcat] = useState({});

  const categorias = [...new Set(products.map(p => p.categoria))].sort((a, b) => {
    if (a === "Pedagógicos · STEM") return 1;
    if (b === "Pedagógicos · STEM") return -1;
    return 0;
  });

  return (
    <div style={{ position:"relative", zIndex:20, padding:"8px 16px 24px", background:"rgba(10,10,12,.3)", backdropFilter:"blur(2px)", borderTop:"1px solid rgba(200,176,122,.2)" }}>
      {categorias.map(cat => {
        const grupo = products.filter(p => p.categoria === cat);
        const subcats = [...new Set(grupo.map(p => p.subcategoria).filter(Boolean))];
        const activeSub = activeSubcat[cat] || null;
        const visiveis = activeSub ? grupo.filter(p => p.subcategoria === activeSub) : grupo;

        return (
          <div key={cat} id={catId(cat)} style={{ marginBottom:36 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, maxWidth:1400, margin:"28px auto 14px" }}>
              <div style={{ width:3, height:20, background:GOLD, borderRadius:2, flexShrink:0 }}/>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, letterSpacing:"0.08em", color:"#f0e8dc" }}>{cat.toUpperCase()}</div>
            </div>

            {subcats.length > 1 && (
              <SubcatTabs
                subcats={subcats}
                active={activeSub}
                onSelect={sub => setActiveSubcat(prev => ({ ...prev, [cat]: sub }))}
              />
            )}

            <div style={{
              display:"grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))",
              gap:10,
              maxWidth:1400,
              margin:"0 auto",
            }}>
              {visiveis.map(p => (
                <ProductCard key={p.id} prod={p} onSelect={onSelect} favoritos={favoritos} onFavorito={onFavorito} onBuy={onBuy} cart={cart} user={user}/>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════
   MODAL PRODUTO
══════════════════════════════ */
function Overlay({ children, onClose }) {
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.88)", backdropFilter:"blur(12px)", zIndex:600, display:"flex", alignItems:"center", justifyContent:"center" }}>
      {children}
    </div>
  );
}

const CORES = [
  { id:"preto",        label:"Preto Matte",   hex:"#1c1c1c" },
  { id:"branco",       label:"Branco Matte",  hex:"#e8e4dc" },
  { id:"cinza",        label:"Cinza",         hex:"#7f8c8d" },
  { id:"prata",        label:"Prata",         hex:"#aeb6be" },
  { id:"vermelho",     label:"Vermelho",      hex:"#c0392b" },
  { id:"laranja",      label:"Laranja",       hex:"#e67e22" },
  { id:"amarelo",      label:"Amarelo",       hex:"#f1c40f" },
  { id:"verde-oliva",  label:"Verde Oliva",   hex:"#4a5240" },
  { id:"verde",        label:"Verde",         hex:"#27ae60" },
  { id:"verde-neon",   label:"Verde Neon",    hex:"#39ff14" },
  { id:"azul-marinho", label:"Azul Marinho",  hex:"#1a2f5e" },
  { id:"azul",         label:"Azul",          hex:"#2980b9" },
  { id:"azul-ceu",     label:"Azul Céu",      hex:"#5dade2" },
  { id:"roxo",         label:"Roxo",          hex:"#8e44ad" },
  { id:"rosa",         label:"Rosa",          hex:"#e91e8c" },
  { id:"rosa-claro",   label:"Rosa Claro",    hex:"#f8a5c2" },
  { id:"marrom",       label:"Marrom",        hex:"#7d4e27" },
  { id:"bege",         label:"Bege",          hex:"#d4b896" },
  { id:"dourado",      label:"Dourado",       hex:"#c8b07a" },
  { id:"transparente", label:"Transparente",  hex:"rgba(255,255,255,0.08)" },
];

function ColorPalette({ selected, onToggle }) {
  const cheia = selected.length >= 4;
  return (
    <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
      {CORES.map(cor => {
        const idx = selected.findIndex(c => c.id === cor.id);
        const ativa = idx >= 0;
        const desabilitada = !ativa && cheia;
        return (
          <button
            key={cor.id}
            title={desabilitada ? "Máx. 4 cores" : cor.label}
            onClick={() => !desabilitada && onToggle(cor)}
            style={{
              position:"relative",
              width:34, height:34, borderRadius:"50%", padding:0, flexShrink:0,
              background: cor.id === "transparente"
                ? "repeating-conic-gradient(#555 0% 25%,#333 0% 50%) 0 0/8px 8px"
                : cor.hex,
              border: ativa ? `2.5px solid ${GOLD}` : "2px solid rgba(255,255,255,.12)",
              boxShadow: ativa ? `0 0 0 3px rgba(200,176,122,.35)` : "none",
              transform: ativa ? "scale(1.12)" : "scale(1)",
              opacity: desabilitada ? 0.3 : 1,
              cursor: desabilitada ? "not-allowed" : "pointer",
              transition:"all .18s",
            }}
          >
            {ativa && (
              <div style={{
                position:"absolute", top:-5, right:-5,
                width:16, height:16, borderRadius:"50%",
                background:GOLD, color:"#0a0a0c",
                fontSize:10, fontFamily:"'Bebas Neue',sans-serif",
                display:"flex", alignItems:"center", justifyContent:"center",
                pointerEvents:"none", lineHeight:1,
              }}>{idx + 1}</div>
            )}
          </button>
        );
      })}
    </div>
  );
}

function ProductModal({ prod, onClose, onBuy }) {
  const [corPadrao, setCorPadrao]                 = useState(true);
  const [coresSelecionadas, setCoresSelecionadas] = useState([CORES[0]]);
  const [quantidade, setQuantidade]               = useState(1);
  const [textoPersonalizado, setTextoPersonalizado] = useState("");
  const [observacoes, setObservacoes]             = useState("");
  const [opcaoSelecionada, setOpcaoSelecionada]   = useState(prod?.opcoes?.[0] || null);

  if (!prod) return null;

  const opcaoExtra = (opcaoSelecionada && typeof opcaoSelecionada === "object") ? opcaoSelecionada.extra : 0;
  const totalItem = (prod.price + opcaoExtra) * quantidade;

  return (
    <Overlay onClose={onClose}>
      <div style={{
        background:"#111115", borderRadius:28, width:"92%", maxWidth:420,
        padding:24, border:"1px solid rgba(200,176,122,.4)",
        animation:"fadeSlideUp .3s ease", maxHeight:"90vh", overflowY:"auto",
      }}>
        {/* Imagem + nome */}
        <div style={{ display:"flex", gap:16, alignItems:"center", marginBottom:20 }}>
          <img src={prod.img} alt={prod.name} style={{ width:90, height:90, borderRadius:16, objectFit:"cover", border:"2px solid "+GOLD, flexShrink:0 }}/>
          <div>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:"#f0e8dc", lineHeight:1 }}>{prod.name}</div>
            <div style={{ display:"flex", alignItems:"baseline", gap:8, marginTop:6 }}>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:GOLD }}>{fmtPrice(prod.price)}</div>
              {prod.precoOriginal && <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:13, color:"rgba(240,232,220,.35)", textDecoration:"line-through" }}>{fmtPrice(prod.precoOriginal)}</div>}
            </div>
            {prod.precoOriginal && (
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:9, fontWeight:700, color:"#fff", background:"#e05050", borderRadius:20, padding:"2px 8px", width:"fit-content", letterSpacing:"0.05em", marginTop:4 }}>POR TEMPO LIMITADO</div>
            )}
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:11, color:"rgba(200,176,122,.5)", marginTop:4, letterSpacing:"0.1em" }}>
              {prod.categoria.toUpperCase()}{prod.subcategoria ? ` · ${prod.subcategoria.toUpperCase()}` : ""}
            </div>
          </div>
        </div>

        {/* Divisor */}
        <div style={{ height:1, background:"rgba(200,176,122,.15)", marginBottom:18 }}/>

        {/* Descrição longa */}
        {(prod.descLonga || prod.beneficios) && (
          <div style={{ marginBottom:18, padding:"12px 14px", background:"rgba(200,176,122,.04)", borderRadius:12, border:"1px solid rgba(200,176,122,.1)" }}>
            {prod.descLonga && <p style={{ fontFamily:"'Barlow',sans-serif", fontSize:12, color:"rgba(240,232,220,.7)", lineHeight:1.6, margin:0, marginBottom: prod.beneficios ? 10 : 0 }}>{prod.descLonga}</p>}
            {prod.beneficios && (
              <ul style={{ margin:0, padding:0, listStyle:"none" }}>
                {prod.beneficios.map((b, i) => (
                  <li key={i} style={{ display:"flex", alignItems:"flex-start", gap:7, fontFamily:"'Barlow Condensed',sans-serif", fontSize:12, color:"rgba(240,232,220,.65)", lineHeight:1.4, marginBottom: i < prod.beneficios.length - 1 ? 5 : 0 }}>
                    <span style={{ color:GOLD, fontSize:14, lineHeight:1.2, flexShrink:0 }}>·</span>
                    {b}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Opções do produto */}
        {prod.opcoes && (
          <div style={{ marginBottom:18 }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"0.2em", color:"rgba(200,176,122,.6)", marginBottom:10 }}>
              OPÇÃO
            </div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {prod.opcoes.map(op => {
                const opLabel = typeof op === "object" ? op.label : op;
                const opExtra = typeof op === "object" ? op.extra : 0;
                const ativa = typeof op === "object"
                  ? (opcaoSelecionada && typeof opcaoSelecionada === "object" && opcaoSelecionada.label === op.label)
                  : opcaoSelecionada === op;
                return (
                  <button key={opLabel} type="button" onClick={() => setOpcaoSelecionada(op)}
                    style={{
                      padding:"8px 16px", borderRadius:30, cursor:"pointer",
                      fontFamily:"'Barlow Condensed',sans-serif", fontSize:13, letterSpacing:"0.08em",
                      border: ativa ? `1.5px solid ${GOLD}` : "1px solid rgba(200,176,122,.25)",
                      background: ativa ? "rgba(200,176,122,.18)" : "rgba(255,255,255,.03)",
                      color: ativa ? GOLD : "rgba(240,232,220,.65)",
                      fontWeight: ativa ? 700 : "normal",
                      transition:"all .2s",
                    }}>
                    {opLabel}{opExtra > 0 && <span style={{ fontSize:11, color: ativa ? GOLD : "rgba(200,176,122,.5)", marginLeft:4 }}>+{fmtPrice(opExtra)}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Seletor de cor */}
        <div style={{ marginBottom:20 }}>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"0.2em", color:"rgba(200,176,122,.6)", marginBottom:10 }}>
            ESCOLHA AS CORES
          </div>

          {/* Opção padrão */}
          <button
            onClick={() => setCorPadrao(true)}
            style={{
              display:"flex", alignItems:"center", gap:10,
              width:"100%", marginBottom:10, padding:"10px 14px",
              borderRadius:12, cursor:"pointer", transition:"all .2s",
              border: corPadrao ? `1.5px solid ${GOLD}` : "1px solid rgba(200,176,122,.2)",
              background: corPadrao ? "rgba(200,176,122,.1)" : "rgba(255,255,255,.02)",
            }}
          >
            <img src={prod.img} alt="" style={{ width:32, height:32, borderRadius:8, objectFit:"cover", border:"1px solid rgba(200,176,122,.3)", flexShrink:0 }}/>
            <div style={{ textAlign:"left" }}>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:13, color: corPadrao ? "#f0e8dc" : "rgba(240,232,220,.6)", letterSpacing:"0.06em" }}>
                Cor padrão <span style={{ color: corPadrao ? GOLD : "rgba(200,176,122,.4)", fontSize:11 }}>(como na foto)</span>
              </div>
            </div>
            {corPadrao && (
              <svg viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2.5" width={14} height={14} style={{ marginLeft:"auto", flexShrink:0 }}>
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            )}
          </button>

          {/* Separador */}
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
            <div style={{ flex:1, height:1, background:"rgba(200,176,122,.12)" }}/>
            <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:10, color:"rgba(200,176,122,.35)", letterSpacing:"0.15em" }}>OU ESCOLHA</span>
            <div style={{ flex:1, height:1, background:"rgba(200,176,122,.12)" }}/>
          </div>

          {/* Paleta personalizada */}
          <div style={{ opacity: corPadrao ? 0.6 : 1, transition:"opacity .2s" }}>
            {/* Preview */}
            {!corPadrao && (
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                {coresSelecionadas.map((cor, i) => (
                  <div key={cor.id} style={{ position:"relative" }}>
                    <div style={{
                      width:24, height:24, borderRadius:"50%",
                      background: cor.id === "transparente" ? "repeating-conic-gradient(#555 0% 25%,#333 0% 50%) 0 0/8px 8px" : cor.hex,
                      border:"1.5px solid rgba(255,255,255,.2)",
                    }}/>
                    <div style={{
                      position:"absolute", top:-4, right:-4, width:13, height:13, borderRadius:"50%",
                      background:GOLD, color:"#0a0a0c", fontSize:8,
                      fontFamily:"'Bebas Neue',sans-serif",
                      display:"flex", alignItems:"center", justifyContent:"center",
                    }}>{i + 1}</div>
                  </div>
                ))}
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:11, color:"rgba(240,232,220,.6)" }}>
                  {coresSelecionadas.map(c => c.label).join(" + ")}
                </div>
              </div>
            )}
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"0.2em", color:"rgba(200,176,122,.5)", marginBottom:8 }}>
              PERSONALIZAR CORES <span style={{ fontWeight:400, color:"rgba(200,176,122,.3)" }}>(ATÉ 4)</span>
            </div>
            <ColorPalette
              selected={coresSelecionadas}
              onToggle={cor => {
                setCorPadrao(false);
                setCoresSelecionadas(prev => {
                  const idx = prev.findIndex(c => c.id === cor.id);
                  if (idx >= 0) {
                    const nova = prev.filter(c => c.id !== cor.id);
                    return nova.length === 0 ? prev : nova;
                  }
                  if (prev.length >= 4) return prev;
                  return [...prev, cor];
                });
              }}
            />
          </div>

          <div style={{ marginTop:10, fontFamily:"'Barlow',sans-serif", fontSize:11, color:"rgba(200,176,122,.4)" }}>
            Escolha gratuita · impresso sob encomenda · prazo combinado no pedido
          </div>
        </div>

        {prod.categoria !== "Educacionais" && (<>
          {/* Divisor */}
          <div style={{ height:1, background:"rgba(200,176,122,.15)", marginBottom:18 }}/>

          {/* Personalização */}
          <div style={{ marginBottom:20 }}>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.2em", color:"rgba(200,176,122,.6)", marginBottom:10 }}>
              PERSONALIZAÇÃO <span style={{ fontWeight:400, color:"rgba(200,176,122,.35)" }}>(OPCIONAL)</span>
            </div>
            <input
              type="text"
              placeholder="Texto ou nome para gravar / imprimir..."
              aria-label="Texto ou nome para gravar ou imprimir"
              value={textoPersonalizado}
              onChange={e => setTextoPersonalizado(e.target.value)}
              style={{
                width:"100%", background:"rgba(255,255,255,.03)",
                border:"1px solid rgba(200,176,122,.22)", borderRadius:12,
                padding:"10px 14px", marginBottom:8,
                fontFamily:"'Barlow',sans-serif", fontSize:13,
                color:"#f0e8dc", outline:"none",
              }}
            />
            <textarea
              placeholder="Outras personalizações (cor específica, tamanho, presente, instruções)..."
              aria-label="Outras personalizações"
              value={observacoes}
              onChange={e => setObservacoes(e.target.value)}
              rows={3}
              style={{
                width:"100%", background:"rgba(255,255,255,.03)",
                border:"1px solid rgba(200,176,122,.22)", borderRadius:12,
                padding:"10px 14px", resize:"vertical",
                fontFamily:"'Barlow',sans-serif", fontSize:13,
                color:"#f0e8dc", outline:"none", lineHeight:1.5,
              }}
            />
          </div>
        </>)}

        {/* Divisor */}
        <div style={{ height:1, background:"rgba(200,176,122,.15)", marginBottom:18 }}/>

        {/* Seletor de quantidade */}
        <div style={{ marginBottom:22 }}>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.2em", color:"rgba(200,176,122,.6)", marginBottom:10 }}>
            QUANTIDADE
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:16 }}>
            <div style={{ display:"flex", alignItems:"center", border:"1px solid rgba(200,176,122,.3)", borderRadius:12, overflow:"hidden" }}>
              <button
                onClick={() => setQuantidade(q => Math.max(1, q - 1))}
                aria-label="Diminuir quantidade"
                style={{ width:42, height:42, background:"rgba(200,176,122,.08)", border:"none", color:"#f0e8dc", fontSize:20, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", transition:"background .2s" }}
                onMouseEnter={e => e.currentTarget.style.background="rgba(200,176,122,.2)"}
                onMouseLeave={e => e.currentTarget.style.background="rgba(200,176,122,.08)"}
              >−</button>
              <div role="status" aria-live="polite" style={{ width:48, textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:"#f0e8dc" }}>
                {quantidade}
              </div>
              <button
                onClick={() => setQuantidade(q => Math.min(99, q + 1))}
                aria-label="Aumentar quantidade"
                style={{ width:42, height:42, background:"rgba(200,176,122,.08)", border:"none", color:"#f0e8dc", fontSize:20, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", transition:"background .2s" }}
                onMouseEnter={e => e.currentTarget.style.background="rgba(200,176,122,.2)"}
                onMouseLeave={e => e.currentTarget.style.background="rgba(200,176,122,.08)"}
              >+</button>
            </div>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:13, color:"rgba(200,176,122,.5)" }}>
              Total: <span style={{ color:GOLD, fontSize:16, fontFamily:"'Bebas Neue',sans-serif" }}>{fmtPrice(totalItem)}</span>
            </div>
          </div>
        </div>

        {/* Prazo de produção */}
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16, padding:"10px 14px", background:"rgba(200,176,122,.05)", borderRadius:12, border:"1px solid rgba(200,176,122,.13)" }}>
          <svg viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width={16} height={16} style={{ flexShrink:0 }}>
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
          <div style={{ fontFamily:"'Barlow',sans-serif", fontSize:12, color:"rgba(240,232,220,.6)", lineHeight:1.4 }}>
            Pronto em <strong style={{ color:GOLD }}>5 a 7 dias úteis</strong> após confirmação do pedido (o prazo pode variar)
          </div>
        </div>

        {/* Botões */}
        <div style={{ display:"flex", gap:10 }}>
          {prod.tipoVenda === "sob-consulta" ? (
            <button
              onClick={() => {
                const corLabel = corPadrao ? "Cor padrão (como na foto)" : coresSelecionadas.map(c => c.label).join(" + ");
                const linhas = [
                  `Olá! Tenho interesse em encomendar:`,
                  ``,
                  `Produto: ${prod.name}`,
                  `Quantidade: ${quantidade}`,
                  `Cor: ${corLabel}`,
                  textoPersonalizado.trim() ? `Personalização: ${textoPersonalizado.trim()}` : null,
                  observacoes.trim() ? `Observações: ${observacoes.trim()}` : null,
                  ``,
                  `Qual o prazo e como faço para pagar?`,
                ].filter(l => l !== null);
                window.open(waLink(linhas.join("\n")), "_blank");
                onClose();
              }}
              style={{ flex:1, background:"#25D366", color:"#fff", border:"none", padding:"12px 0", borderRadius:40, cursor:"pointer", fontFamily:"'Bebas Neue',sans-serif", fontSize:15, fontWeight:"bold", letterSpacing:"0.1em", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
              <svg viewBox="0 0 24 24" fill="white" width={17} height={17}>
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              CONSULTAR VIA WHATSAPP
            </button>
          ) : (
            <button
              onClick={() => { const opLabel = opcaoSelecionada ? (typeof opcaoSelecionada === "object" ? opcaoSelecionada.label : opcaoSelecionada) : null; onBuy({ ...prod, cor: corPadrao ? "Cor padrão (como na foto)" : coresSelecionadas.map(c => c.label).join(" + "), quantidade, textoPersonalizado: textoPersonalizado.trim() || null, observacoes: observacoes.trim() || null, opcao: opLabel, opcaoExtra }); onClose(); }}
              style={{ flex:1, background:GOLD, color:"#0a0a0c", border:"none", padding:"12px 0", borderRadius:40, cursor:"pointer", fontFamily:"'Bebas Neue',sans-serif", fontSize:15, fontWeight:"bold", letterSpacing:"0.1em" }}>
              ADICIONAR AO CARRINHO
            </button>
          )}
          <button
            onClick={onClose}
            style={{ background:"transparent", border:"1px solid rgba(200,176,122,.4)", padding:"12px 16px", borderRadius:40, color:"#f0e8dc", cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontSize:13, letterSpacing:"0.1em", flexShrink:0 }}>
            VOLTAR
          </button>
        </div>
      </div>
    </Overlay>
  );
}

/* ══════════════════════════════
   COMO FUNCIONA
══════════════════════════════ */
/* ══════════════════════════════
   DESCONTO POR VOLUME
══════════════════════════════ */
function DescontoTiersStrip() {
  const tiers = [
    { pct:"20%", valor:"R$ 300+" },
    { pct:"30%", valor:"R$ 600+" },
    { pct:"40%", valor:"R$ 1.200+" },
  ];
  return (
    <div style={{ maxWidth:1400, margin:"0 auto", padding:"0 16px 20px", position:"relative", zIndex:10 }}>
      <div style={{ background:"rgba(200,176,122,.05)", border:"1px solid rgba(200,176,122,.18)", borderRadius:10, padding:"9px 12px", display:"flex", alignItems:"center", gap:10, flexWrap:"nowrap", overflow:"hidden" }}>
        {/* Label esquerdo */}
        <div className="strip-label">
          <svg viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width={12} height={12} style={{ flexShrink:0 }}>
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>
          </svg>
          <span className="strip-label-text">DESCONTO POR VOLUME</span>
        </div>
        {/* Divisor esquerdo */}
        <div className="strip-divider-left"/>
        {/* Tiers em linha */}
        <div style={{ display:"flex", alignItems:"baseline", gap:0, flex:1, justifyContent:"center" }}>
          {tiers.map((t, i) => (
            <div key={t.pct} style={{ display:"flex", alignItems:"baseline", gap:3, padding:"0 10px", borderRight: i < 2 ? "1px solid rgba(200,176,122,.15)" : "none" }}>
              <span className="strip-pct">{t.pct}</span>
              <span className="strip-off">OFF · {t.valor}</span>
            </div>
          ))}
        </div>
        {/* Divisor + confirmação — some no celular */}
        <div className="strip-right">
          <div style={{ width:1, height:14, background:"rgba(200,176,122,.2)" }}/>
          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="rgba(37,211,102,.8)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width={10} height={10}><polyline points="20 6 9 17 4 12"/></svg>
            <span className="strip-confirm">Aplicado automaticamente</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ComoFuncionaSection() {
  const passos = [
    {
      num: "01",
      titulo: "Escolha o produto",
      desc: "Navegue pelo catálogo, filtre por categoria e clique no produto que chamou atenção.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width={26} height={26}>
          <path d="M3 9l1-7h16l1 7"/><path d="M3 9a2 2 0 004 0 2 2 0 004 0 2 2 0 004 0 2 2 0 004 0"/>
          <path d="M5 9v11a1 1 0 001 1h12a1 1 0 001-1V9"/><rect x="9" y="14" width="6" height="6"/>
        </svg>
      ),
    },
    {
      num: "02",
      titulo: "Personalize",
      desc: "Escolha a cor (ou combine até 4). Adicione texto, nome ou outras instruções.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width={26} height={26}>
          <circle cx="13.5" cy="6.5" r=".5"/><circle cx="17.5" cy="10.5" r=".5"/>
          <circle cx="8.5" cy="7.5" r=".5"/><circle cx="6.5" cy="12.5" r=".5"/>
          <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 011.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/>
        </svg>
      ),
    },
    {
      num: "03",
      titulo: "Pague com segurança",
      desc: "Cartão de crédito, débito ou Pix direto pelo Mercado Pago. Rápido e sem burocracia.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width={26} height={26}>
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
        </svg>
      ),
    },
    {
      num: "04",
      titulo: "Receba em casa",
      desc: "Produzimos e enviamos para todo o Brasil. Prazo de 5 a 7 dias úteis após confirmação.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width={26} height={26}>
          <rect x="1" y="3" width="15" height="13"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
        </svg>
      ),
    },
  ];

  return (
    <div style={{ padding:"40px 16px 12px", maxWidth:1400, margin:"0 auto", position:"relative", zIndex:10 }}>
      <div style={{ textAlign:"center", marginBottom:32 }}>
        <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:10, letterSpacing:"0.25em", color:GOLD, marginBottom:8 }}>SIMPLES ASSIM</div>
        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:34, color:"#f0e8dc", lineHeight:1 }}>COMO FUNCIONA</div>
      </div>
      <div style={{ display:"flex", gap:12, flexWrap:"wrap", justifyContent:"center" }}>
        {passos.map((p, i) => (
          <div key={p.num} style={{ flex:"1 1 180px", maxWidth:260, background:"rgba(10,10,12,.85)", backdropFilter:"blur(10px)", border:"1px solid rgba(200,176,122,.2)", borderRadius:20, padding:"22px 18px", display:"flex", flexDirection:"column", gap:12, position:"relative" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ color:GOLD, flexShrink:0 }}>{p.icon}</div>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:36, color:"rgba(200,176,122,.15)", lineHeight:1 }}>{p.num}</div>
            </div>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:17, color:"#f0e8dc", letterSpacing:"0.03em" }}>{p.titulo}</div>
            <div style={{ fontFamily:"'Barlow',sans-serif", fontSize:12, color:"rgba(200,176,122,.7)", lineHeight:1.6 }}>{p.desc}</div>
            {i < passos.length - 1 && (
              <div style={{ position:"absolute", right:-10, top:"50%", transform:"translateY(-50%)", fontSize:22, color:"rgba(200,176,122,.2)", zIndex:1 }}>›</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════
   FLOATING WHATSAPP
══════════════════════════════ */
function FloatingWhatsApp() {
  return (
    <a
      href={waLink("Olá! Tenho interesse nos produtos HYBRIDBOTS!")}
      target="_blank"
      rel="noopener noreferrer"
      title="Falar no WhatsApp"
      aria-label="Falar no WhatsApp"
      style={{
        position:"fixed", bottom:24, right:20, zIndex:9999,
        background:"#25D366", borderRadius:"50%", width:56, height:56,
        display:"flex", alignItems:"center", justifyContent:"center",
        animation:"waPulse 2.5s ease-in-out infinite",
        textDecoration:"none", flexShrink:0,
      }}
    >
      <svg viewBox="0 0 24 24" fill="white" width={28} height={28}>
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </a>
  );
}



/* ══════════════════════════════
   GOOGLE LOGIN BTN
══════════════════════════════ */
function GoogleLoginBtn({ onSuccess, onError }) {
  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });
        const profile = await res.json();
        // Cadastra ou loga via servidor
        const r = await fetch("https://hybridbotsloja3d-production.up.railway.app/google-login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            nome: profile.name,
            email: profile.email,
            google_id: profile.sub,
          }),
        });
        const data = await r.json();
        if (!r.ok) { onError(data.erro || "Erro ao fazer login com Google."); return; }
        onSuccess({ name: data.nome, email: data.email });
      } catch {
        onError("Erro ao conectar com Google.");
      }
    },
    onError: () => onError("Login com Google cancelado."),
  });

  return (
    <button onClick={() => login()}
      style={{ width:"100%", padding:"11px 0", borderRadius:12, border:"1px solid rgba(200,176,122,.2)", background:"rgba(255,255,255,.02)", cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontSize:13, letterSpacing:"0.08em", color:"rgba(240,232,220,.7)", display:"flex", alignItems:"center", justifyContent:"center", gap:10, transition:"all .2s" }}
      onMouseEnter={e => { e.currentTarget.style.borderColor="rgba(200,176,122,.4)"; e.currentTarget.style.background="rgba(200,176,122,.06)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor="rgba(200,176,122,.2)"; e.currentTarget.style.background="rgba(255,255,255,.02)"; }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      CONTINUAR COM GOOGLE
    </button>
  );
}

/* ══════════════════════════════
   AUTH MODAL
══════════════════════════════ */
function MsgBox({ text, type }) {
  return (
    <div style={{ background: type==="error" ? "rgba(220,80,60,.12)" : "rgba(60,180,100,.12)", border:`1px solid ${type==="error" ? "rgba(220,80,60,.3)" : "rgba(60,180,100,.3)"}`, borderRadius:10, padding:"9px 13px", fontSize:12, color: type==="error" ? "#f07060" : "#60d888", marginBottom:14, fontFamily:"'Barlow',sans-serif" }}>
      {text}
    </div>
  );
}

function FormField({ label, icon, children, id }) {
  const icons = {
    mail: <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>,
    lock: <><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
    user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
  };
  return (
    <div style={{ marginBottom:14 }}>
      <label htmlFor={id} style={{ display:"block", fontFamily:"'Barlow Condensed',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"0.2em", color:"rgba(200,176,122,.6)", marginBottom:6 }}>{label}</label>
      <div style={{ position:"relative" }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="rgba(200,176,122,.4)" strokeWidth="1.5" width={16} height={16}
          aria-hidden="true"
          style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}>
          {icons[icon]}
        </svg>
        {children}
      </div>
    </div>
  );
}

const inputStyle = {
  width:"100%", background:"rgba(255,255,255,.03)",
  border:"1px solid rgba(200,176,122,.22)", borderRadius:12,
  padding:"11px 14px 11px 40px",
  fontFamily:"'Barlow',sans-serif", fontSize:14, color:"#f0e8dc", outline:"none",
};

function EyeBtn({ show, onToggle }) {
  return (
    <button type="button" onClick={onToggle} aria-label={show ? "Ocultar senha" : "Mostrar senha"}
      style={{ position:"absolute", right:13, top:"50%", transform:"translateY(-50%)", cursor:"pointer", background:"none", border:"none", padding:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <svg viewBox="0 0 24 24" fill="none" stroke="rgba(200,176,122,.4)" strokeWidth="1.5" width={16} height={16} aria-hidden="true">
        {show
          ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>
          : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
        }
      </svg>
    </button>
  );
}

function SubmitBtn({ onClick, children }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ width:"100%", padding:13, border:"none", borderRadius:14, background: hov ? "linear-gradient(135deg,#d4be8c,#e0cc96)" : "linear-gradient(135deg,#c8b07a,#d4be8c)", fontFamily:"'Bebas Neue',sans-serif", fontSize:17, letterSpacing:"0.15em", color:"#0a0a0c", cursor:"pointer", transition:"all .25s", boxShadow: hov ? "0 6px 20px rgba(200,176,122,.35)" : "none", transform: hov ? "translateY(-1px)" : "none" }}>
      {children}
    </button>
  );
}

function AuthModal({ onClose, onSuccess }) {
  const [tab, setTab]           = useState("login");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPw, setLoginPw]       = useState("");
  const [showLoginPw, setShowLoginPw] = useState(false);
  const [loginErr, setLoginErr]     = useState("");
  const [loginOk, setLoginOk]       = useState("");
  const [regName, setRegName]       = useState("");
  const [regEmail, setRegEmail]     = useState("");
  const [regPw, setRegPw]           = useState("");
  const [regConfirm, setRegConfirm] = useState("");
  const [showRegPw, setShowRegPw]   = useState(false);
  const [regErr, setRegErr]         = useState("");
  const [regOk, setRegOk]           = useState("");

  async function doLogin() {
    setLoginErr(""); setLoginOk("");
    if (!loginEmail || !loginPw) { setLoginErr("Preencha e-mail e senha."); return; }
    if (!/\S+@\S+\.\S+/.test(loginEmail)) { setLoginErr("E-mail inválido."); return; }
    try {
      const res = await fetch("https://hybridbotsloja3d-production.up.railway.app/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: loginEmail, senha: loginPw }),
      });
      const data = await res.json();
      if (!res.ok) { setLoginErr(data.erro || "E-mail ou senha incorretos."); return; }
      setLoginOk(`Bem-vindo de volta, ${data.nome.split(" ")[0]}! 🎉`);
      setTimeout(() => { onSuccess({ name: data.nome, email: data.email }); onClose(); }, 800);
    } catch { setLoginErr("Servidor offline. Tente novamente."); }
  }

  async function doRegister() {
    setRegErr(""); setRegOk("");
    if (!regName || !regEmail || !regPw || !regConfirm) { setRegErr("Preencha todos os campos."); return; }
    if (regName.length < 2) { setRegErr("Nome muito curto."); return; }
    if (!/\S+@\S+\.\S+/.test(regEmail)) { setRegErr("E-mail inválido."); return; }
    if (regPw.length < 6) { setRegErr("A senha deve ter ao menos 6 caracteres."); return; }
    if (regPw !== regConfirm) { setRegErr("As senhas não coincidem."); return; }
    try {
      const res = await fetch("https://hybridbotsloja3d-production.up.railway.app/cadastro", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome: regName, email: regEmail, senha: regPw }),
      });
      const data = await res.json();
      if (!res.ok) { setRegErr(data.erro || "Erro ao criar conta."); return; }
      setRegOk("Conta criada com sucesso! 🚀");
      setTimeout(() => { onSuccess({ name: data.nome, email: data.email }); onClose(); }, 900);
    } catch { setRegErr("Servidor offline. Tente novamente."); }
  }

  return (
    <Overlay onClose={onClose}>
      <div style={{ background:"linear-gradient(160deg,#111116,#0d0d10)", borderRadius:28, border:"1px solid rgba(200,176,122,.35)", width:"90%", maxWidth:400, overflow:"hidden", boxShadow:"0 30px 80px rgba(0,0,0,.7)", animation:"fadeSlideUp .35s cubic-bezier(.22,1,.36,1)" }}>
        <div style={{ height:4, background:"linear-gradient(90deg,transparent,#c8b07a,#e8d8a8,#c8b07a,transparent)" }}/>
        <div style={{ padding:"20px 24px 0", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:36, height:36, borderRadius:10, background:"rgba(200,176,122,.15)", border:"1px solid rgba(200,176,122,.4)", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <svg viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="1.5" width={18} height={18}><path d="M12 3L2 7l10 4 10-4-10-4zM2 17l10 4 10-4M2 12l10 4 10-4"/></svg>
            </div>
            <div>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:15, letterSpacing:"0.1em", color:GOLD }}>3D PRINT STORE</div>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:9, color:"rgba(200,176,122,.5)", letterSpacing:"0.2em" }}>ACESSO SEGURO</div>
            </div>
          </div>
          <button onClick={onClose} aria-label="Fechar" style={{ width:32, height:32, borderRadius:"50%", border:"1px solid rgba(200,176,122,.2)", background:"transparent", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#a09080" }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width={14} height={14}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div style={{ display:"flex", margin:"18px 24px 0", borderRadius:12, background:"rgba(255,255,255,.03)", border:"1px solid rgba(200,176,122,.12)", padding:3, gap:3 }}>
          {["login","register"].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{ flex:1, padding:"9px 0", border: tab===t ? "1px solid rgba(200,176,122,.35)" : "none", borderRadius:9, background: tab===t ? "rgba(200,176,122,.18)" : "transparent", fontFamily:"'Barlow Condensed',sans-serif", fontSize:13, fontWeight:700, letterSpacing:"0.15em", color: tab===t ? GOLD : "rgba(200,176,122,.45)", cursor:"pointer" }}>
              {t === "login" ? "ENTRAR" : "CADASTRAR"}
            </button>
          ))}
        </div>

        <div style={{ padding:"20px 24px 28px" }}>
          {tab === "login" ? (
            <>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:26, color:"#f0e8dc" }}>Bem-vindo de volta</div>
              <div style={{ fontFamily:"'Barlow',sans-serif", fontSize:13, color:"rgba(200,176,122,.55)", marginBottom:20 }}>Acesse sua conta para ver pedidos e favoritos</div>
              {loginErr && <MsgBox text={loginErr} type="error"/>}
              {loginOk  && <MsgBox text={loginOk}  type="success"/>}
              <FormField label="E-MAIL" icon="mail" id="login-email">
                <input id="login-email" type="email" placeholder="seu@email.com" value={loginEmail}
                  onChange={e => setLoginEmail(e.target.value)}
                  onKeyDown={e => e.key==="Enter" && doLogin()}
                  style={inputStyle}/>
              </FormField>
              <FormField label="SENHA" icon="lock" id="login-senha">
                <div style={{ position:"relative" }}>
                  <input id="login-senha" type={showLoginPw ? "text" : "password"} placeholder="••••••••" value={loginPw}
                    onChange={e => setLoginPw(e.target.value)}
                    onKeyDown={e => e.key==="Enter" && doLogin()}
                    style={{ ...inputStyle, paddingRight:40 }}/>
                  <EyeBtn show={showLoginPw} onToggle={() => setShowLoginPw(v => !v)}/>
                </div>
              </FormField>
              <div style={{ textAlign:"right", marginTop:-6, marginBottom:14 }}>
                <button type="button" onClick={async () => {
                  if (!loginEmail) { setLoginErr("Digite seu e-mail acima primeiro."); return; }
                  setLoginErr(""); setLoginOk("Enviando...");
                  try {
                    await fetch("https://hybridbotsloja3d-production.up.railway.app/esqueci-senha", {
                      method:"POST", headers:{"Content-Type":"application/json"},
                      body: JSON.stringify({ email: loginEmail }),
                    });
                    setLoginOk(`Link enviado para ${loginEmail}! Verifique sua caixa de entrada.`);
                  } catch { setLoginErr("Servidor offline."); }
                }} style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:11, color:"rgba(200,176,122,.5)", cursor:"pointer", background:"none", border:"none", padding:0 }}>
                  Esqueci minha senha
                </button>
              </div>
              <SubmitBtn onClick={doLogin}>ENTRAR NA CONTA</SubmitBtn>
              <div style={{ display:"flex", alignItems:"center", gap:12, margin:"16px 0" }}>
                <div style={{ flex:1, height:1, background:"rgba(200,176,122,.15)" }}/>
                <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:11, color:"rgba(200,176,122,.35)", letterSpacing:"0.1em" }}>OU ENTRE COM</span>
                <div style={{ flex:1, height:1, background:"rgba(200,176,122,.15)" }}/>
              </div>
              <GoogleLoginBtn onSuccess={(userData) => { onSuccess(userData); onClose(); }} onError={(msg) => setLoginErr(msg)}/>
            </>
          ) : (
            <>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:26, color:"#f0e8dc" }}>Crie sua conta</div>
              <div style={{ fontFamily:"'Barlow',sans-serif", fontSize:13, color:"rgba(200,176,122,.55)", marginBottom:20 }}>Acompanhe pedidos e receba ofertas exclusivas</div>
              {regErr && <MsgBox text={regErr} type="error"/>}
              {regOk  && <MsgBox text={regOk}  type="success"/>}
              <FormField label="SEU NOME" icon="user" id="reg-nome">
                <input id="reg-nome" type="text" placeholder="João Silva" value={regName} onChange={e => setRegName(e.target.value)} style={inputStyle}/>
              </FormField>
              <FormField label="E-MAIL" icon="mail" id="reg-email">
                <input id="reg-email" type="email" placeholder="seu@email.com" value={regEmail} onChange={e => setRegEmail(e.target.value)} style={inputStyle}/>
              </FormField>
              <FormField label="SENHA" icon="lock" id="reg-senha">
                <div style={{ position:"relative" }}>
                  <input id="reg-senha" type={showRegPw ? "text" : "password"} placeholder="Mín. 6 caracteres" value={regPw} onChange={e => setRegPw(e.target.value)} style={{ ...inputStyle, paddingRight:40 }}/>
                  <EyeBtn show={showRegPw} onToggle={() => setShowRegPw(v => !v)}/>
                </div>
              </FormField>
              <FormField label="CONFIRMAR SENHA" icon="lock" id="reg-confirmar">
                <input id="reg-confirmar" type="password" placeholder="Repita a senha" value={regConfirm}
                  onChange={e => setRegConfirm(e.target.value)}
                  onKeyDown={e => e.key==="Enter" && doRegister()}
                  style={inputStyle}/>
              </FormField>
              <SubmitBtn onClick={doRegister}>CRIAR CONTA</SubmitBtn>
            </>
          )}
        </div>
      </div>
    </Overlay>
  );
}

/* ══════════════════════════════
   MEUS PEDIDOS
══════════════════════════════ */
function MeusPedidos({ user, open, onClose }) {
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    setLoading(true);
    fetch(`https://hybridbotsloja3d-production.up.railway.app/pedidos/${encodeURIComponent(user.email)}`)
      .then(r => r.json())
      .then(data => { setPedidos(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [open, user]);

  const statusLabel = {
    aguardando_pagamento: { label:"Aguardando pagamento", color:"#e0a030" },
    pago:                 { label:"Pago ✓",               color:"#4CAF50" },
    enviado:              { label:"Enviado 📦",            color:"#4090e0" },
    entregue:             { label:"Entregue ✅",           color:"#4CAF50" },
    cancelado:            { label:"Cancelado ✗",           color:"#e06050" },
  };

  return (
    <div style={{ position:"fixed", top:0, right:0, width:"90%", maxWidth:440, height:"100vh", background:"#0c0c0e", borderLeft:"1px solid rgba(200,176,122,.2)", zIndex:400, display:"flex", flexDirection:"column", transform: open ? "translateX(0)" : "translateX(100%)", transition:"transform .4s cubic-bezier(.22,1,.36,1)" }}>
      <div style={{ height:3, background:"linear-gradient(90deg,transparent,#c8b07a,#e8d8a8,#c8b07a,transparent)" }}/>
      <div style={{ padding:20, display:"flex", alignItems:"center", justifyContent:"space-between", borderBottom:"1px solid rgba(200,176,122,.15)" }}>
        <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, letterSpacing:"0.1em", color:GOLD }}>MEUS PEDIDOS</span>
        <button onClick={onClose} aria-label="Fechar" style={{ width:32, height:32, borderRadius:"50%", border:"1px solid rgba(200,176,122,.2)", background:"transparent", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#a09080" }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width={14} height={14}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <div style={{ flex:1, overflowY:"auto", padding:"16px 20px" }}>
        {loading && (
          <div style={{ textAlign:"center", marginTop:40, color:"rgba(200,176,122,.5)", fontFamily:"'Barlow Condensed',sans-serif", fontSize:14 }}>
            Carregando pedidos...
          </div>
        )}
        {!loading && pedidos.length === 0 && (
          <div style={{ textAlign:"center", marginTop:60 }}>
            <div style={{ fontSize:40, marginBottom:12 }}>📦</div>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:"#f0e8dc" }}>Nenhum pedido ainda</div>
            <div style={{ fontFamily:"'Barlow',sans-serif", fontSize:13, color:"rgba(200,176,122,.5)", marginTop:8 }}>Seus pedidos aparecerão aqui após a compra</div>
          </div>
        )}
        {!loading && pedidos.map(pedido => {
          const st = statusLabel[pedido.status] || { label: pedido.status, color: GOLD };
          const data = new Date(pedido.criado_em).toLocaleDateString("pt-BR");
          const hora = new Date(pedido.criado_em).toLocaleTimeString("pt-BR", { hour:"2-digit", minute:"2-digit" });
          return (
            <div key={pedido.id} style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(200,176,122,.2)", borderRadius:16, padding:16, marginBottom:14 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:11, color:"rgba(200,176,122,.5)", letterSpacing:"0.1em" }}>
                  {data} às {hora}
                </div>
                <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:11, fontWeight:700, color: st.color, background:`${st.color}20`, borderRadius:20, padding:"3px 10px", letterSpacing:"0.05em" }}>
                  {st.label}
                </span>
              </div>

              {Array.isArray(pedido.itens) && pedido.itens.map((item, i) => (
                <div key={i} style={{ display:"flex", gap:10, marginBottom:8, alignItems:"center" }}>
                  <img src={item.img} alt={item.name} style={{ width:40, height:40, borderRadius:8, objectFit:"cover", border:"1px solid rgba(200,176,122,.2)", flexShrink:0 }}/>
                  <div style={{ flex:1 }}>
                    <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:13, color:"#f0e8dc" }}>{item.name}</div>
                    {item.cor && <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:11, color:"rgba(200,176,122,.5)" }}>{item.cor}</div>}
                    <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:11, color:GOLD }}>{item.quantity}x {`R$ ${item.price.toFixed(2)}`}</div>
                  </div>
                </div>
              ))}

              <div style={{ borderTop:"1px solid rgba(200,176,122,.1)", paddingTop:10, marginTop:6 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:12, color:"rgba(200,176,122,.5)" }}>TOTAL</span>
                  <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:16, color:GOLD }}>{`R$ ${Number(pedido.total).toFixed(2)}`}</span>
                </div>
                {pedido.codigo_rastreio && (
                  <div style={{ marginTop:10, background:"rgba(64,144,224,.08)", border:"1px solid rgba(64,144,224,.3)", borderRadius:10, padding:"10px 12px" }}>
                    <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:10, letterSpacing:"0.15em", color:"rgba(64,144,224,.7)", marginBottom:4 }}>CÓDIGO DE RASTREIO</div>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                      <span style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:14, fontWeight:700, color:"#f0e8dc", letterSpacing:"0.1em" }}>{pedido.codigo_rastreio}</span>
                      <button
                        onClick={() => window.open(`https://rastreamento.correios.com.br/app/index.php?objetos=${pedido.codigo_rastreio}`, "_blank")}
                        style={{ background:"rgba(64,144,224,.2)", border:"1px solid rgba(64,144,224,.5)", borderRadius:20, padding:"5px 12px", fontFamily:"'Barlow Condensed',sans-serif", fontSize:11, letterSpacing:"0.1em", color:"#4090e0", cursor:"pointer", whiteSpace:"nowrap", transition:"all .2s" }}
                        onMouseEnter={e => e.currentTarget.style.background="rgba(64,144,224,.35)"}
                        onMouseLeave={e => e.currentTarget.style.background="rgba(64,144,224,.2)"}
                      >
                        📦 RASTREAR
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ══════════════════════════════
   EDITAR PERFIL
══════════════════════════════ */
function EditarPerfil({ user, open, onClose, onUpdate }) {
  const [nome, setNome]           = useState("");
  const [senhaAtual, setSenhaAtual] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmar, setConfirmar] = useState("");
  const [showSenha, setShowSenha] = useState(false);
  const [err, setErr]             = useState("");
  const [ok, setOk]               = useState("");
  const [loading, setLoading]     = useState(false);

  useEffect(() => {
    if (open && user) { setNome(user.name); setErr(""); setOk(""); setSenhaAtual(""); setNovaSenha(""); setConfirmar(""); }
  }, [open, user]);

  async function salvar() {
    setErr(""); setOk("");
    if (!nome || nome.trim().length < 2) { setErr("Nome muito curto."); return; }
    if (novaSenha && novaSenha.length < 6) { setErr("Nova senha deve ter ao menos 6 caracteres."); return; }
    if (novaSenha && novaSenha !== confirmar) { setErr("As senhas não coincidem."); return; }
    if (novaSenha && !senhaAtual) { setErr("Digite sua senha atual para alterá-la."); return; }

    setLoading(true);
    try {
      const res = await fetch("https://hybridbotsloja3d-production.up.railway.app/editar-perfil", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: user.email,
          nome: nome.trim(),
          senhaAtual: senhaAtual || undefined,
          novaSenha: novaSenha || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setErr(data.erro || "Erro ao salvar."); setLoading(false); return; }
      setOk("Perfil atualizado com sucesso! ✓");
      onUpdate({ name: data.nome, email: user.email });
      setLoading(false);
    } catch { setErr("Servidor offline. Tente novamente."); setLoading(false); }
  }

  return (
    <div style={{ position:"fixed", top:0, right:0, width:"90%", maxWidth:400, height:"100vh", background:"#0c0c0e", borderLeft:"1px solid rgba(200,176,122,.2)", zIndex:400, display:"flex", flexDirection:"column", transform: open ? "translateX(0)" : "translateX(100%)", transition:"transform .4s cubic-bezier(.22,1,.36,1)" }}>
      <div style={{ height:3, background:"linear-gradient(90deg,transparent,#c8b07a,#e8d8a8,#c8b07a,transparent)" }}/>
      <div style={{ padding:20, display:"flex", alignItems:"center", justifyContent:"space-between", borderBottom:"1px solid rgba(200,176,122,.15)" }}>
        <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, letterSpacing:"0.1em", color:GOLD }}>EDITAR PERFIL</span>
        <button onClick={onClose} aria-label="Fechar" style={{ width:32, height:32, borderRadius:"50%", border:"1px solid rgba(200,176,122,.2)", background:"transparent", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#a09080" }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width={14} height={14}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <div style={{ flex:1, overflowY:"auto", padding:"24px 20px" }}>
        {/* Avatar */}
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", marginBottom:28 }}>
          <div style={{ width:72, height:72, borderRadius:"50%", background:"linear-gradient(135deg,rgba(200,176,122,.3),rgba(200,176,122,.08))", border:"2px solid "+GOLD, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:28, color:GOLD }}>
            {user ? getInitials(nome || user.name) : "?"}
          </div>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:11, color:"rgba(200,176,122,.4)", marginTop:8, letterSpacing:"0.1em" }}>{user?.email}</div>
        </div>

        {err && <div style={{ background:"rgba(220,80,60,.12)", border:"1px solid rgba(220,80,60,.3)", borderRadius:10, padding:"9px 13px", fontSize:12, color:"#f07060", marginBottom:14, fontFamily:"'Barlow',sans-serif" }}>{err}</div>}
        {ok  && <div style={{ background:"rgba(60,180,100,.12)", border:"1px solid rgba(60,180,100,.3)", borderRadius:10, padding:"9px 13px", fontSize:12, color:"#60d888", marginBottom:14, fontFamily:"'Barlow',sans-serif" }}>{ok}</div>}

        {/* Nome */}
        <div style={{ marginBottom:14 }}>
          <label style={{ display:"block", fontFamily:"'Barlow Condensed',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"0.2em", color:"rgba(200,176,122,.6)", marginBottom:6 }}>SEU NOME</label>
          <div style={{ position:"relative" }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="rgba(200,176,122,.4)" strokeWidth="1.5" width={16} height={16} style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}>
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
            </svg>
            <input type="text" value={nome} onChange={e => setNome(e.target.value)} placeholder="Seu nome completo" style={inputStyle}/>
          </div>
        </div>

        {/* Divisor */}
        <div style={{ height:1, background:"rgba(200,176,122,.1)", margin:"20px 0", position:"relative" }}>
          <span style={{ position:"absolute", left:"50%", transform:"translateX(-50%) translateY(-50%)", background:"#0c0c0e", padding:"0 10px", fontFamily:"'Barlow Condensed',sans-serif", fontSize:10, color:"rgba(200,176,122,.4)", letterSpacing:"0.15em" }}>ALTERAR SENHA</span>
        </div>

        {/* Senha atual */}
        <div style={{ marginBottom:14 }}>
          <label style={{ display:"block", fontFamily:"'Barlow Condensed',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"0.2em", color:"rgba(200,176,122,.6)", marginBottom:6 }}>SENHA ATUAL</label>
          <div style={{ position:"relative" }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="rgba(200,176,122,.4)" strokeWidth="1.5" width={16} height={16} style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}>
              <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <input type={showSenha ? "text" : "password"} value={senhaAtual} onChange={e => setSenhaAtual(e.target.value)} placeholder="Digite sua senha atual" style={{ ...inputStyle, paddingRight:40 }}/>
            <EyeBtn show={showSenha} onToggle={() => setShowSenha(v => !v)}/>
          </div>
        </div>

        {/* Nova senha */}
        <div style={{ marginBottom:14 }}>
          <label style={{ display:"block", fontFamily:"'Barlow Condensed',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"0.2em", color:"rgba(200,176,122,.6)", marginBottom:6 }}>NOVA SENHA</label>
          <div style={{ position:"relative" }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="rgba(200,176,122,.4)" strokeWidth="1.5" width={16} height={16} style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}>
              <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <input type="password" value={novaSenha} onChange={e => setNovaSenha(e.target.value)} placeholder="Mín. 6 caracteres" style={inputStyle}/>
          </div>
        </div>

        {/* Confirmar senha */}
        <div style={{ marginBottom:24 }}>
          <label style={{ display:"block", fontFamily:"'Barlow Condensed',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"0.2em", color:"rgba(200,176,122,.6)", marginBottom:6 }}>CONFIRMAR NOVA SENHA</label>
          <div style={{ position:"relative" }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="rgba(200,176,122,.4)" strokeWidth="1.5" width={16} height={16} style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}>
              <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <input type="password" value={confirmar} onChange={e => setConfirmar(e.target.value)} placeholder="Repita a nova senha" style={inputStyle} onKeyDown={e => e.key==="Enter" && salvar()}/>
          </div>
        </div>

        <button onClick={salvar} disabled={loading} style={{ width:"100%", padding:13, border:"none", borderRadius:14, background: loading ? "rgba(200,176,122,.4)" : "linear-gradient(135deg,#c8b07a,#d4be8c)", fontFamily:"'Bebas Neue',sans-serif", fontSize:17, letterSpacing:"0.15em", color:"#0a0a0c", cursor: loading ? "not-allowed" : "pointer", transition:"all .25s" }}>
          {loading ? "SALVANDO..." : "SALVAR ALTERAÇÕES"}
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════
   MEUS FAVORITOS
══════════════════════════════ */
function MeusFavoritos({ user, open, onClose, favoritos = [], onFavorito, onSelect }) {
  const produtosFav = PRODUCTS.filter(p => favoritos.includes(p.id));

  return (
    <div style={{ position:"fixed", top:0, right:0, width:"90%", maxWidth:440, height:"100vh", background:"#0c0c0e", borderLeft:"1px solid rgba(200,176,122,.2)", zIndex:400, display:"flex", flexDirection:"column", transform: open ? "translateX(0)" : "translateX(100%)", transition:"transform .4s cubic-bezier(.22,1,.36,1)" }}>
      <div style={{ height:3, background:"linear-gradient(90deg,transparent,#c8b07a,#e8d8a8,#c8b07a,transparent)" }}/>
      <div style={{ padding:20, display:"flex", alignItems:"center", justifyContent:"space-between", borderBottom:"1px solid rgba(200,176,122,.15)" }}>
        <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, letterSpacing:"0.1em", color:GOLD }}>FAVORITOS</span>
        <button onClick={onClose} aria-label="Fechar" style={{ width:32, height:32, borderRadius:"50%", border:"1px solid rgba(200,176,122,.2)", background:"transparent", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#a09080" }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width={14} height={14}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <div style={{ flex:1, overflowY:"auto", padding:"16px 20px" }}>
        {produtosFav.length === 0 ? (
          <div style={{ textAlign:"center", marginTop:60 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="rgba(200,176,122,.3)" strokeWidth="1.5" width={48} height={48} style={{ display:"block", margin:"0 auto 16px" }}>
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:"#f0e8dc" }}>Nenhum favorito ainda</div>
            <div style={{ fontFamily:"'Barlow',sans-serif", fontSize:13, color:"rgba(200,176,122,.5)", marginTop:8 }}>Clique no coração nos produtos para favoritar</div>
          </div>
        ) : (
          produtosFav.map(prod => (
            <div key={prod.id} style={{ display:"flex", gap:12, marginBottom:14, background:"rgba(255,255,255,.03)", border:"1px solid rgba(200,176,122,.2)", borderRadius:14, padding:12, alignItems:"center" }}>
              <img src={prod.img} alt={prod.name} style={{ width:56, height:56, borderRadius:10, objectFit:"cover", border:"1px solid rgba(200,176,122,.2)", flexShrink:0, cursor:"pointer" }} onClick={() => { onClose(); onSelect(prod); }}/>
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:15, color:"#f0e8dc", cursor:"pointer" }} onClick={() => { onClose(); onSelect(prod); }}>{prod.name}</div>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:13, color:GOLD, marginTop:2 }}>{fmtPrice(prod.price)}</div>
              </div>
              <button onClick={() => onFavorito(prod.id)} title="Remover dos favoritos"
                style={{ width:34, height:34, borderRadius:"50%", border:"1px solid rgba(220,80,80,.5)", background:"rgba(220,80,80,.1)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <svg viewBox="0 0 24 24" fill="#e05050" stroke="#e05050" strokeWidth="1.8" width={15} height={15}>
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════
   CONFIGURAÇÕES
══════════════════════════════ */
function Configuracoes({ user, open, onClose, onSave }) {
  const [cep, setCep]               = useState("");
  const [endereco, setEndereco]     = useState("");
  const [numero, setNumero]         = useState("");
  const [complemento, setComplemento] = useState("");
  const [bairro, setBairro]         = useState("");
  const [cidade, setCidade]         = useState("");
  const [estado, setEstado]         = useState("");
  const [err, setErr]               = useState("");
  const [ok, setOk]                 = useState("");
  const [loading, setLoading]       = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    setErr(""); setOk("");
    fetch(`https://hybridbotsloja3d-production.up.railway.app/configuracoes/${encodeURIComponent(user.email)}`)
      .then(r => r.json())
      .then(d => {
        setCep(d.cep || ""); setEndereco(d.endereco || "");
        setNumero(d.numero || ""); setComplemento(d.complemento || "");
        setBairro(d.bairro || ""); setCidade(d.cidade || ""); setEstado(d.estado || "");
      }).catch(() => {});
  }, [open, user]);

  async function buscarCep(v) {
    const c = v.replace(/\D/g, "");
    setCep(v);
    if (c.length !== 8) return;
    setBuscandoCep(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${c}/json/`);
      const d = await r.json();
      if (!d.erro) {
        setEndereco(d.logradouro || "");
        setBairro(d.bairro || "");
        setCidade(d.localidade || "");
        setEstado(d.uf || "");
      }
    } catch {}
    setBuscandoCep(false);
  }

  async function salvar() {
    setErr(""); setOk("");
    if (!cep || !endereco || !numero || !cidade || !estado) {
      setErr("Preencha CEP, endereço, número, cidade e estado."); return;
    }
    setLoading(true);
    try {
      const res = await fetch("https://hybridbotsloja3d-production.up.railway.app/configuracoes", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ email: user.email, cep, endereco, numero, complemento, bairro, cidade, estado }),
      });
      const data = await res.json();
      if (!res.ok) { setErr(data.erro || "Erro ao salvar."); setLoading(false); return; }
      setOk("Endereço salvo com sucesso!");
      onSave("Endereço de entrega salvo!");
    } catch { setErr("Servidor offline."); }
    setLoading(false);
  }

  const estados = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

  const fieldStyle = { ...inputStyle, paddingLeft:14 };

  return (
    <div style={{ position:"fixed", top:0, right:0, width:"90%", maxWidth:420, height:"100vh", background:"#0c0c0e", borderLeft:"1px solid rgba(200,176,122,.2)", zIndex:400, display:"flex", flexDirection:"column", transform: open ? "translateX(0)" : "translateX(100%)", transition:"transform .4s cubic-bezier(.22,1,.36,1)" }}>
      <div style={{ height:3, background:"linear-gradient(90deg,transparent,#c8b07a,#e8d8a8,#c8b07a,transparent)" }}/>
      <div style={{ padding:20, display:"flex", alignItems:"center", justifyContent:"space-between", borderBottom:"1px solid rgba(200,176,122,.15)" }}>
        <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, letterSpacing:"0.1em", color:GOLD }}>ENDEREÇO DE ENTREGA</span>
        <button onClick={onClose} aria-label="Fechar" style={{ width:32, height:32, borderRadius:"50%", border:"1px solid rgba(200,176,122,.2)", background:"transparent", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#a09080" }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width={14} height={14}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <div style={{ flex:1, overflowY:"auto", padding:"20px" }}>
        {err && <div style={{ background:"rgba(220,80,60,.12)", border:"1px solid rgba(220,80,60,.3)", borderRadius:10, padding:"9px 13px", fontSize:12, color:"#f07060", marginBottom:14, fontFamily:"'Barlow',sans-serif" }}>{err}</div>}
        {ok  && <div style={{ background:"rgba(60,180,100,.12)", border:"1px solid rgba(60,180,100,.3)", borderRadius:10, padding:"9px 13px", fontSize:12, color:"#60d888", marginBottom:14, fontFamily:"'Barlow',sans-serif" }}>{ok}</div>}

        {/* CEP */}
        <div style={{ marginBottom:14 }}>
          <label style={{ display:"block", fontFamily:"'Barlow Condensed',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"0.2em", color:"rgba(200,176,122,.6)", marginBottom:6 }}>CEP {buscandoCep && <span style={{ color:GOLD }}>buscando...</span>}</label>
          <input type="text" value={cep} onChange={e => buscarCep(e.target.value)} placeholder="00000-000" maxLength={9} style={fieldStyle}/>
        </div>

        {/* Endereço */}
        <div style={{ marginBottom:14 }}>
          <label style={{ display:"block", fontFamily:"'Barlow Condensed',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"0.2em", color:"rgba(200,176,122,.6)", marginBottom:6 }}>ENDEREÇO</label>
          <input type="text" value={endereco} onChange={e => setEndereco(e.target.value)} placeholder="Rua, Avenida..." style={fieldStyle}/>
        </div>

        {/* Número + Complemento */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
          <div>
            <label style={{ display:"block", fontFamily:"'Barlow Condensed',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"0.2em", color:"rgba(200,176,122,.6)", marginBottom:6 }}>NÚMERO</label>
            <input type="text" value={numero} onChange={e => setNumero(e.target.value)} placeholder="123" style={fieldStyle}/>
          </div>
          <div>
            <label style={{ display:"block", fontFamily:"'Barlow Condensed',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"0.2em", color:"rgba(200,176,122,.6)", marginBottom:6 }}>COMPLEMENTO</label>
            <input type="text" value={complemento} onChange={e => setComplemento(e.target.value)} placeholder="Apto, Bloco..." style={fieldStyle}/>
          </div>
        </div>

        {/* Bairro */}
        <div style={{ marginBottom:14 }}>
          <label style={{ display:"block", fontFamily:"'Barlow Condensed',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"0.2em", color:"rgba(200,176,122,.6)", marginBottom:6 }}>BAIRRO</label>
          <input type="text" value={bairro} onChange={e => setBairro(e.target.value)} placeholder="Bairro" style={fieldStyle}/>
        </div>

        {/* Cidade + Estado */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 80px", gap:10, marginBottom:24 }}>
          <div>
            <label style={{ display:"block", fontFamily:"'Barlow Condensed',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"0.2em", color:"rgba(200,176,122,.6)", marginBottom:6 }}>CIDADE</label>
            <input type="text" value={cidade} onChange={e => setCidade(e.target.value)} placeholder="Cidade" style={fieldStyle}/>
          </div>
          <div>
            <label style={{ display:"block", fontFamily:"'Barlow Condensed',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"0.2em", color:"rgba(200,176,122,.6)", marginBottom:6 }}>ESTADO</label>
            <select value={estado} onChange={e => setEstado(e.target.value)}
              style={{ ...fieldStyle, paddingLeft:8, cursor:"pointer" }}>
              <option value="">UF</option>
              {estados.map(uf => <option key={uf} value={uf}>{uf}</option>)}
            </select>
          </div>
        </div>

        <button onClick={salvar} disabled={loading} style={{ width:"100%", padding:13, border:"none", borderRadius:14, background: loading ? "rgba(200,176,122,.4)" : "linear-gradient(135deg,#c8b07a,#d4be8c)", fontFamily:"'Bebas Neue',sans-serif", fontSize:17, letterSpacing:"0.15em", color:"#0a0a0c", cursor: loading ? "not-allowed" : "pointer" }}>
          {loading ? "SALVANDO..." : "SALVAR ENDEREÇO"}
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════
   PROFILE SIDEBAR
══════════════════════════════ */
function ProfileSidebar({ user, open, onClose, onLogout, onPedidos, onEditar, onFavoritos, onConfiguracoes }) {
  const menuItems = [
    { label:"MEUS PEDIDOS",  path:<><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></>, action:"pedidos" },
    { label:"FAVORITOS",     path:<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>, action:"favoritos" },
    { label:"EDITAR PERFIL", path:<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>, action:"editar" },
    { label:"CONFIGURAÇÕES", path:<><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06A1.65 1.65 0 0 0 15 19.4a1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></>, action:"configuracoes" },
  ];
  return (
    <div style={{ position:"fixed", top:0, right:0, width:"85%", maxWidth:360, height:"100vh", background:"#0c0c0e", borderLeft:"1px solid rgba(200,176,122,.2)", zIndex:300, display:"flex", flexDirection:"column", transform: open ? "translateX(0)" : "translateX(100%)", transition:"transform .4s cubic-bezier(.22,1,.36,1)", overflowY:"auto" }}>
      <div style={{ height:3, background:"linear-gradient(90deg,transparent,#c8b07a,#e8d8a8,#c8b07a,transparent)" }}/>
      <div style={{ padding:20, display:"flex", alignItems:"center", justifyContent:"space-between", borderBottom:"1px solid rgba(200,176,122,.15)" }}>
        <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, letterSpacing:"0.1em", color:GOLD }}>MINHA CONTA</span>
        <button onClick={onClose} aria-label="Fechar" style={{ width:32, height:32, borderRadius:"50%", border:"1px solid rgba(200,176,122,.2)", background:"transparent", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#a09080" }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width={14} height={14}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
      <div style={{ padding:"28px 20px 20px", display:"flex", flexDirection:"column", alignItems:"center", gap:12, borderBottom:"1px solid rgba(200,176,122,.1)" }}>
        <div style={{ width:80, height:80, borderRadius:"50%", background:"linear-gradient(135deg,rgba(200,176,122,.3),rgba(200,176,122,.08))", border:"2px solid "+GOLD, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:32, color:GOLD, position:"relative" }}>
          {user ? getInitials(user.name) : "?"}
          <span style={{ position:"absolute", bottom:4, right:4, width:14, height:14, background:"#4CAF50", borderRadius:"50%", border:"2px solid #0c0c0e" }}/>
        </div>
        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:22, color:"#f0e8dc" }}>{user?.name}</div>
        <div style={{ fontFamily:"'Barlow',sans-serif", fontSize:13, color:"rgba(200,176,122,.55)" }}>{user?.email}</div>
      </div>
      <div style={{ padding:"16px 20px", flex:1 }}>
        {menuItems.map(item => (
          <button type="button" key={item.label}
            onMouseEnter={e => { e.currentTarget.style.background="rgba(200,176,122,.08)"; e.currentTarget.style.color="#f0e8dc"; }}
            onMouseLeave={e => { e.currentTarget.style.background="transparent"; e.currentTarget.style.color="rgba(240,232,220,.75)"; }}
            onClick={() => { if(item.action==="pedidos") onPedidos(); if(item.action==="editar") onEditar(); if(item.action==="favoritos") onFavoritos(); if(item.action==="configuracoes") onConfiguracoes(); }}
            style={{ display:"flex", alignItems:"center", gap:14, padding:"13px 16px", borderRadius:14, cursor:"pointer", marginBottom:4, fontFamily:"'Barlow Condensed',sans-serif", fontSize:15, letterSpacing:"0.08em", color:"rgba(240,232,220,.75)", transition:"all .2s", background:"transparent", border:"none", width:"100%", textAlign:"left" }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="rgba(200,176,122,.6)" strokeWidth="1.5" width={18} height={18} aria-hidden="true">{item.path}</svg>
            {item.label}
          </button>
        ))}
      </div>
      <button onClick={onLogout} style={{ margin:"0 20px 20px", padding:12, border:"1.5px solid rgba(220,70,60,.4)", borderRadius:14, background:"rgba(220,70,60,.07)", fontFamily:"'Bebas Neue',sans-serif", fontSize:15, letterSpacing:"0.15em", color:"#e06050", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width={16} height={16}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
        SAIR DA CONTA
      </button>
    </div>
  );
}

/* ══════════════════════════════
   CART SIDEBAR
══════════════════════════════ */
function CartSidebar({ open, onClose, cart, onRemove, onCheckout }) {
  const calcItemTotal = (i) => {
    const extra = i.opcaoExtra || 0;
    const precoUnit = i.price + extra;
    if (i.promo2x && i.quantity >= 2) {
      const pares = Math.floor(i.quantity / 2);
      const resto = i.quantity % 2;
      return pares * i.promo2x + resto * precoUnit;
    }
    return precoUnit * i.quantity;
  };
  const totalBruto = cart.reduce((a,i) => a + calcItemTotal(i), 0);
  const desc       = calcDesconto(totalBruto);
  const descValor  = desc ? +(totalBruto * desc.pct).toFixed(2) : 0;
  const totalFinal = +(totalBruto - descValor).toFixed(2);

  const nextThreshold = !desc ? 300 : desc.next;
  const nextPct       = !desc ? "20%" : desc.pct === 0.20 ? "30%" : "40%";
  const faltaReais    = nextThreshold ? +(nextThreshold - totalBruto).toFixed(2) : null;

  return (
    <div style={{ position:"fixed", top:0, right:0, width:"85%", maxWidth:360, height:"100vh", background:"#0c0c0e", borderLeft:"1px solid rgba(200,176,122,.2)", zIndex:300, display:"flex", flexDirection:"column", padding:20, transform: open ? "translateX(0)" : "translateX(100%)", transition:"transform .4s cubic-bezier(.22,1,.36,1)" }}>
      <div style={{ display:"flex", justifyContent:"space-between", borderBottom:"1px solid rgba(200,176,122,.2)", paddingBottom:10, marginBottom:16, fontFamily:"'Bebas Neue',sans-serif", color:"#f0e8dc", letterSpacing:"0.1em", alignItems:"center" }}>
        <span>SEU CARRINHO</span>
        <button onClick={onClose} aria-label="Fechar carrinho" style={{ cursor:"pointer", color:GOLD, fontSize:13, fontFamily:"'Barlow Condensed',sans-serif", background:"none", border:"none", padding:0 }}>FECHAR</button>
      </div>
      <div style={{ flex:1, overflowY:"auto" }}>
        {cart.length === 0
          ? <div style={{ textAlign:"center", marginTop:30, color:"#7e6e5a", fontFamily:"'Barlow Condensed',sans-serif" }}>Carrinho vazio.</div>
          : cart.map(item => (
            <div key={item._key || item.id} style={{ display:"flex", gap:10, marginBottom:14, borderBottom:"1px solid rgba(200,176,122,.1)", paddingBottom:10 }}>
              <div style={{ width:50, height:50, borderRadius:10, backgroundImage:`url('${item.img}')`, backgroundSize:"cover", flexShrink:0 }}/>
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif", color:"#f0e8dc", fontSize:14 }}>{item.name}</div>
                {item.opcao && <div style={{ fontFamily:"'Barlow Condensed',sans-serif", color:GOLD, fontSize:11, marginTop:1 }}>{item.opcao}</div>}
                {item.cor && <div style={{ fontFamily:"'Barlow Condensed',sans-serif", color:"rgba(200,176,122,.5)", fontSize:11, marginTop:1 }}>{item.cor}</div>}
                {item.textoPersonalizado && <div style={{ fontFamily:"'Barlow Condensed',sans-serif", color:"rgba(200,176,122,.6)", fontSize:11, marginTop:1 }}>✏ {item.textoPersonalizado}</div>}
                {item.observacoes && <div style={{ fontFamily:"'Barlow',sans-serif", color:"rgba(200,176,122,.4)", fontSize:10, marginTop:1, fontStyle:"italic" }}>{item.observacoes}</div>}
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", color:GOLD, fontSize:12, marginTop:2 }}>{fmtPrice(item.price)} × {item.quantity} = {fmtPrice(item.price * item.quantity)}</div>
                <button type="button" onClick={() => onRemove(item._key || item.id)} aria-label={`Remover ${item.name} do carrinho`} style={{ fontSize:9, color:"#a09080", cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", background:"none", border:"none", padding:0 }}>✕ remover</button>
              </div>
            </div>
          ))
        }
      </div>
      <div>
        {faltaReais > 0 && (
          <div style={{ background:"rgba(200,176,122,.05)", border:"1px solid rgba(200,176,122,.15)", borderRadius:10, padding:"8px 12px", marginBottom:10, fontFamily:"'Barlow Condensed',sans-serif", fontSize:11, color:"rgba(200,176,122,.7)", textAlign:"center" }}>
            Faltam {fmtPrice(faltaReais)} para ganhar {nextPct} de desconto em todo o pedido
          </div>
        )}
        {desc && (
          <div style={{ background:"rgba(37,211,102,.08)", border:"1px solid rgba(37,211,102,.28)", borderRadius:10, padding:"8px 12px", marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span style={{ color:"#25d366", fontFamily:"'Barlow Condensed',sans-serif", fontSize:13, fontWeight:700, letterSpacing:"0.06em" }}>
              {desc.label}
            </span>
            <span style={{ color:"#25d366", fontFamily:"'Barlow Condensed',sans-serif", fontSize:12 }}>
              -{fmtPrice(descValor)}
            </span>
          </div>
        )}
        {desc && (
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4, fontFamily:"'Barlow Condensed',sans-serif", color:"rgba(200,176,122,.4)", fontSize:13 }}>
            <span>SUBTOTAL</span><span style={{ textDecoration:"line-through" }}>{fmtPrice(totalBruto)}</span>
          </div>
        )}
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:10, fontFamily:"'Barlow Condensed',sans-serif", color:GOLD, fontSize:16 }}>
          <span>TOTAL</span><span>{fmtPrice(totalFinal)}</span>
        </div>
        <button onClick={onCheckout} style={{ background:GOLD, width:"100%", padding:10, border:"none", borderRadius:8, cursor:"pointer", fontFamily:"'Bebas Neue',sans-serif", fontSize:16, letterSpacing:"0.1em" }}>
          FINALIZAR COMPRA
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════
   APP
══════════════════════════════ */
/* ══════════════════════════════
   RESET SENHA MODAL
══════════════════════════════ */
function ResetSenhaModal({ token, onClose }) {
  const [novaSenha, setNovaSenha]   = useState("");
  const [confirmar, setConfirmar]   = useState("");
  const [show, setShow]             = useState(false);
  const [err, setErr]               = useState("");
  const [ok, setOk]                 = useState("");
  const [loading, setLoading]       = useState(false);

  async function redefinir() {
    setErr(""); setOk("");
    if (!novaSenha || !confirmar) { setErr("Preencha os dois campos."); return; }
    if (novaSenha.length < 6) { setErr("A senha deve ter ao menos 6 caracteres."); return; }
    if (novaSenha !== confirmar) { setErr("As senhas não coincidem."); return; }
    setLoading(true);
    try {
      const res = await fetch("https://hybridbotsloja3d-production.up.railway.app/redefinir-senha", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ token, novaSenha }),
      });
      const data = await res.json();
      if (!res.ok) { setErr(data.erro || "Erro ao redefinir."); setLoading(false); return; }
      setOk("Senha redefinida com sucesso! Faça login com a nova senha.");
      setTimeout(onClose, 2500);
    } catch { setErr("Servidor offline."); }
    setLoading(false);
  }

  return (
    <Overlay onClose={onClose}>
      <div style={{ background:"linear-gradient(160deg,#111116,#0d0d10)", borderRadius:28, border:"1px solid rgba(200,176,122,.35)", width:"90%", maxWidth:380, overflow:"hidden", boxShadow:"0 30px 80px rgba(0,0,0,.7)", animation:"fadeSlideUp .35s cubic-bezier(.22,1,.36,1)" }}>
        <div style={{ height:4, background:"linear-gradient(90deg,transparent,#c8b07a,#e8d8a8,#c8b07a,transparent)" }}/>
        <div style={{ padding:"24px 24px 28px" }}>
          <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:24, color:"#f0e8dc", marginBottom:6 }}>REDEFINIR SENHA</div>
          <div style={{ fontFamily:"'Barlow',sans-serif", fontSize:13, color:"rgba(200,176,122,.55)", marginBottom:20 }}>Digite sua nova senha abaixo</div>

          {err && <div style={{ background:"rgba(220,80,60,.12)", border:"1px solid rgba(220,80,60,.3)", borderRadius:10, padding:"9px 13px", fontSize:12, color:"#f07060", marginBottom:14, fontFamily:"'Barlow',sans-serif" }}>{err}</div>}
          {ok  && <div style={{ background:"rgba(60,180,100,.12)", border:"1px solid rgba(60,180,100,.3)", borderRadius:10, padding:"9px 13px", fontSize:12, color:"#60d888", marginBottom:14, fontFamily:"'Barlow',sans-serif" }}>{ok}</div>}

          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block", fontFamily:"'Barlow Condensed',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"0.2em", color:"rgba(200,176,122,.6)", marginBottom:6 }}>NOVA SENHA</label>
            <div style={{ position:"relative" }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="rgba(200,176,122,.4)" strokeWidth="1.5" width={16} height={16} style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}>
                <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              <input type={show ? "text" : "password"} value={novaSenha} onChange={e => setNovaSenha(e.target.value)} placeholder="Mín. 6 caracteres" style={{ ...inputStyle, paddingRight:40 }}/>
              <EyeBtn show={show} onToggle={() => setShow(v => !v)}/>
            </div>
          </div>

          <div style={{ marginBottom:20 }}>
            <label style={{ display:"block", fontFamily:"'Barlow Condensed',sans-serif", fontSize:10, fontWeight:700, letterSpacing:"0.2em", color:"rgba(200,176,122,.6)", marginBottom:6 }}>CONFIRMAR SENHA</label>
            <div style={{ position:"relative" }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="rgba(200,176,122,.4)" strokeWidth="1.5" width={16} height={16} style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}>
                <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              <input type="password" value={confirmar} onChange={e => setConfirmar(e.target.value)} placeholder="Repita a nova senha" style={inputStyle} onKeyDown={e => e.key==="Enter" && redefinir()}/>
            </div>
          </div>

          <button onClick={redefinir} disabled={loading} style={{ width:"100%", padding:13, border:"none", borderRadius:14, background: loading ? "rgba(200,176,122,.4)" : "linear-gradient(135deg,#c8b07a,#d4be8c)", fontFamily:"'Bebas Neue',sans-serif", fontSize:17, letterSpacing:"0.15em", color:"#0a0a0c", cursor: loading ? "not-allowed" : "pointer", marginBottom:10 }}>
            {loading ? "SALVANDO..." : "SALVAR NOVA SENHA"}
          </button>
          <button onClick={onClose} style={{ width:"100%", padding:10, border:"1px solid rgba(200,176,122,.3)", borderRadius:14, background:"transparent", fontFamily:"'Barlow Condensed',sans-serif", fontSize:14, letterSpacing:"0.1em", color:"rgba(200,176,122,.6)", cursor:"pointer" }}>
            CANCELAR
          </button>
        </div>
      </div>
    </Overlay>
  );
}

export default function App() {
  const [user, setUser]             = useState(() => getStoredUser());
  const [favoritos, setFavoritos]     = useState([]);
  const [resetToken, setResetToken] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get("reset") || null;
  });
  const [cart, setCart]             = useState([]);
  const [showAuth, setShowAuth]     = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showPedidos, setShowPedidos] = useState(false);
  const [showEditar, setShowEditar] = useState(false);
  const [showFavoritos, setShowFavoritos] = useState(false);
  const [showConfiguracoes, setShowConfiguracoes] = useState(false);
  const [showCart, setShowCart]     = useState(false);
  const [selectedProd, setSelectedProd] = useState(null);
  const [filtroTexto, setFiltroTexto]         = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [toast, setToast]           = useState({ visible:false, msg:"", icon:"✓" });
  const toastTimer = useRef(null);

  function showToast(msg, icon = "✓") {
    clearTimeout(toastTimer.current);
    setToast({ visible:true, msg, icon });
    toastTimer.current = setTimeout(() => setToast(t => ({ ...t, visible:false })), 2600);
  }

  function handleLogin(u) {
    storeUser(u); setUser(u);
    showToast(`Olá, ${u.name.split(" ")[0]}! Bem-vindo.`, "👋");
    fetch(`https://hybridbotsloja3d-production.up.railway.app/favoritos/${encodeURIComponent(u.email)}`)
      .then(r => r.json()).then(data => setFavoritos(Array.isArray(data) ? data.map(f => f.produto_id) : [])).catch(() => {});
  }
  function handleUpdate(u) { storeUser(u); setUser(u); showToast("Perfil atualizado!", "✓"); }
  function handleLogout() { const n = user?.name?.split(" ")[0] || ""; removeUser(); setUser(null); setShowProfile(false); setFavoritos([]); showToast(`Até logo, ${n}!`, "👋"); }

  async function toggleFavorito(prodId) {
    if (!user) { setShowAuth(true); return; }
    const jaFav = favoritos.includes(prodId);
    if (jaFav) {
      setFavoritos(prev => prev.filter(id => id !== prodId));
      await fetch("https://hybridbotsloja3d-production.up.railway.app/favoritos", {
        method:"DELETE", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ email: user.email, produto_id: prodId }),
      });
    } else {
      setFavoritos(prev => [...prev, prodId]);
      await fetch("https://hybridbotsloja3d-production.up.railway.app/favoritos", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ email: user.email, produto_id: prodId }),
      });
    }
  }

  function addToCart(prod) {
    const key = `${prod.id}-${prod.cor || ""}-${prod.textoPersonalizado || ""}`;
    setCart(prev => {
      const exist = prev.find(i => i._key === key);
      return exist
        ? prev.map(i => i._key === key ? { ...i, quantity: i.quantity + (prod.quantidade || 1) } : i)
        : [...prev, { ...prod, quantity: prod.quantidade || 1, _key: key }];
    });
    setShowCart(true);
    showToast(`${prod.name} adicionado ao carrinho`, "🛒");
  }

  async function checkout() {
    if (cart.length === 0) { showToast("Carrinho vazio!", "⚠️"); return; }
    if (!user) {
      setShowCart(false);
      setShowAuth(true);
      showToast("Faça login para finalizar a compra", "🔒");
      return;
    }
    showToast("Aguarde, redirecionando...", "⏳");
    try {
      const calcItemTotalCO = (i) => {
        if (i.promo2x && i.quantity >= 2) {
          const pares = Math.floor(i.quantity / 2);
          const resto = i.quantity % 2;
          return pares * i.promo2x + resto * i.price;
        }
        return i.price * i.quantity;
      };
      const totalBruto = cart.reduce((a,i) => a + calcItemTotalCO(i), 0);
      const desc = calcDesconto(totalBruto);
      const itensParaMP = cart.map(i => {
        const precoEfetivo = i.promo2x && i.quantity >= 2
          ? +(calcItemTotalCO(i) / i.quantity).toFixed(2)
          : i.price;
        return { ...i, price: desc ? +(precoEfetivo * (1 - desc.pct)).toFixed(2) : precoEfetivo };
      });
      const response = await fetch("https://hybridbotsloja3d-production.up.railway.app/criar-preferencia", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          itens: itensParaMP,
          pagador: { nome: user.name, email: user.email },
        }),
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        showToast("Erro ao processar pagamento.", "❌");
      }
    } catch (err) {
      console.error(err);
      showToast("Servidor offline. Tente novamente.", "❌");
    }
  }

  const cartCount = cart.reduce((s,i) => s+i.quantity, 0);

  const produtosFiltrados = PRODUTOS_TRIAD.filter(p => {
    const textoOk = !filtroTexto || p.name.toLowerCase().includes(filtroTexto.toLowerCase());
    const catOk   = !filtroCategoria || p.categoria === filtroCategoria;
    return textoOk && catOk;
  });

  return (
    <>
      <style>{fontStyle}</style>
      <ThreeBackground/>

      <div style={{ position:"relative", zIndex:1, minHeight:"100vh" }}>
        <Navbar user={user} onLoginClick={() => setShowAuth(true)} onProfileClick={() => setShowProfile(true)} onCartClick={() => setShowCart(v => !v)} cartCount={cartCount}/>
        <Carousel products={PRODUTOS_TRIAD} onSelect={setSelectedProd} favoritos={favoritos} onFavorito={toggleFavorito} user={user}/>
        <TriadSection onFiltroCategoria={setFiltroCategoria}/>
        <FiltroBar
          products={PRODUTOS_TRIAD}
          filtroTexto={filtroTexto}
          onFiltroTexto={setFiltroTexto}
          filtroCategoria={filtroCategoria}
          onFiltroCategoria={setFiltroCategoria}
        />
        <DescontoTiersStrip/>
        <ProductGrid products={produtosFiltrados} onSelect={setSelectedProd} favoritos={favoritos} onFavorito={toggleFavorito} onBuy={addToCart} cart={cart} user={user}/>
        <ComoFuncionaSection/>
        <div style={{ textAlign:"center", padding:"20px 16px 30px" }}>
          <div style={{ fontSize:8, letterSpacing:1, color:"rgba(200,176,122,.6)", fontFamily:"'Barlow Condensed',sans-serif", background:"rgba(0,0,0,.4)", padding:"6px 12px", backdropFilter:"blur(4px)", borderRadius:20, width:"fit-content", margin:"0 auto 14px" }}>
            HYBRIDBOTS · PRODUTOS EXCLUSIVOS
          </div>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:11, color:"rgba(200,176,122,.4)", display:"flex", gap:16, justifyContent:"center", flexWrap:"wrap" }}>
            <span>HYBRIDBOTS · CNPJ 49.930.941/0001-13</span>
            <a href="/privacidade.html" target="_blank" style={{ color:"rgba(200,176,122,.6)", textDecoration:"none" }}>Política de Privacidade</a>
            <a href="/termos.html" target="_blank" style={{ color:"rgba(200,176,122,.6)", textDecoration:"none" }}>Termos de Uso</a>
          </div>
        </div>
      </div>

      {showAuth    && <AuthModal onClose={() => setShowAuth(false)} onSuccess={handleLogin}/>}
      {resetToken  && <ResetSenhaModal token={resetToken} onClose={() => { setResetToken(null); window.history.replaceState({}, "", "/"); }}/>}
      {selectedProd && <ProductModal prod={selectedProd} onClose={() => setSelectedProd(null)} onBuy={addToCart}/>}
      <ProfileSidebar user={user} open={showProfile} onClose={() => setShowProfile(false)} onLogout={handleLogout} onPedidos={() => { setShowProfile(false); setShowPedidos(true); }} onEditar={() => { setShowProfile(false); setShowEditar(true); }} onFavoritos={() => { setShowProfile(false); setShowFavoritos(true); }} onConfiguracoes={() => { setShowProfile(false); setShowConfiguracoes(true); }}/>
      <MeusPedidos user={user} open={showPedidos} onClose={() => setShowPedidos(false)}/>
      <MeusFavoritos user={user} open={showFavoritos} onClose={() => setShowFavoritos(false)} favoritos={favoritos} onFavorito={toggleFavorito} onSelect={setSelectedProd}/>
      <EditarPerfil user={user} open={showEditar} onClose={() => setShowEditar(false)} onUpdate={handleUpdate}/>
      <Configuracoes user={user} open={showConfiguracoes} onClose={() => setShowConfiguracoes(false)} onSave={(msg) => showToast(msg, "✓")}/>
      <CartSidebar open={showCart} onClose={() => setShowCart(false)} cart={cart} onRemove={key => setCart(p => p.filter(i => (i._key || i.id) !== key))} onCheckout={checkout}/>
      {toast.visible && <Toast msg={toast.msg} icon={toast.icon} visible={toast.visible}/>}
      <FloatingWhatsApp/>
    </>
  );
}
